import React, { useState, useMemo, useEffect, useRef, useContext, createContext } from "react";
import TagScanner from "./TagScanner";
import { useTheme } from "./theme";
import {
  api,
  ApiError,
  can as hasPermission,
  eventLabel,
  roleLabel,
  staffStatusLabel,
  statusLabel,
  STAFF_STATUS_LABELS,
  STATUS_LABELS,
} from "./api";

/* ==========================================================================
   ASSET INVENTORY
   --------------------------------------------------------------------------
   Core idea: scan a tag, immediately see who holds the device and whether
   that person is still employed. Admin dashboard registers assets, generates
   tags, assigns holders, and sets both item and employee status.

   Talks to the real API (server/routes.js). The screens below speak a display
   vocabulary — `name`, `category`, `holder`, `since`, and title-case statuses —
   while the database is authoritative and speaks `description`, `category_code`,
   `holder_id`, `held_since`, and lowercase snake_case. Rather than rename a
   thousand lines of markup, the two vocabularies are translated in one place:
   see ADAPTERS below. Every field the UI reads is produced there, and every
   write goes back out through the same layer.

   The server is authoritative about what is allowed. `can()` here only decides
   which controls are worth rendering — a technician who calls a register
   endpoint directly still gets a 403.
   ========================================================================== */

// Change to your real host before printing any labels for actual use.
const ASSET_URL_BASE = "https://assets.solutionsexchange.com/a/";
const ORG_NAME = "SOLUTIONS EXCHANGE INC";

/* ──────────────────────────────────────────────────────────────────────────
   Brother PT-P750W — label geometry

   The P750W prints on continuous TZe tape, not on sheets. There is no grid to
   fill: the tape width fixes the label's height and we choose its length, so
   one label is one page and the printer cuts between pages.

   The page is `@page { size: <tape> <length> }` — portrait, across the tape by
   along it, which is the order P-touch drivers use (the Linux ptouch PPD's
   `tz-12` is 11.6 × 100.2 mm; Brother's Windows driver calls 24 mm tape the
   0.94" paper and takes the length separately). `tape` is the *full* width,
   because that is what the driver calls paper — margins are its business. The
   readable strip is rotated into that page by TapePage, in CSS, so the driver
   never has to rotate or scale anything.

   `print` is the printable height inside the tape — the P-touch feed leaves an
   unprintable margin down both edges — and it bounds the label's content, not
   the page.

   `print` also caps QR resolution in the direction that matters. The print
   head is 180 dpi across the tape and high-resolution mode only doubles
   resolution *along* the tape, so a QR square (height-bound) can never be
   crisper than 180 dpi no matter what the driver is set to. That is why the
   24 mm tape is the only width this asset URL reliably fits on; see
   tapeQrPlan(), which measures it instead of guessing.
   ────────────────────────────────────────────────────────────────────────── */
// `length` is only the default: the operator can change it per batch, and
// surplus length trails off as blank tape because the content is left-aligned.
// Two forces pull against each other here — a longer label prints the asset tag
// larger (see tapeLayout, which shrinks text to fit rather than clipping it),
// while a shorter one saves tape across several hundred labels. 24 mm sits at
// 60 mm because the real register uses 13-character tags, which at 54 mm were
// reduced to 69% of their intended size.
const TAPE_SPECS = [
  { id: "24", tape: 24, print: 18.1, length: 60 },
  { id: "18", tape: 18, print: 12.7, length: 40 },
  { id: "12", tape: 12, print: 9.0, length: 30 },
  { id: "9", tape: 9, print: 6.9, length: 24 },
  { id: "6", tape: 6, print: 3.9, length: 20 },
];
const DEFAULT_TAPE = "24";
const TAPE_STORE_KEY = "ai.tape";

const TAPE_PAD_MM = 0.6; // white kept inside each edge of the printable area
const TAPE_GAP_MM = 1.4; // between the QR block and the text block
const TAPE_LENGTH_MIN = 20;
const TAPE_LENGTH_MAX = 120;

const DOT_MM = 25.4 / 180; // one print-head dot across the tape

const tapeSpec = (id) => TAPE_SPECS.find((s) => s.id === id) ?? TAPE_SPECS[0];

// Brother's Windows driver names its paper sizes in inches — 24 mm tape is the
// "0.94" paper — so the UI has to speak both or nobody can match the two up.
const inches = (mm) => (mm / 25.4).toFixed(2);

/* ==========================================================================
   1. QR ENCODER (byte mode, EC level M, versions 1–6, mask 0)
   Real and scannable. In production: npm i qrcode
   ========================================================================== */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();

const gmul = (a, b) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

function genPoly(n) {
  let g = [1];
  for (let i = 0; i < n; i++) {
    const a = EXP[i];
    const next = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      next[j] ^= g[j];
      next[j + 1] ^= gmul(g[j], a);
    }
    g = next;
  }
  return g;
}

function reedSolomon(data, n) {
  const g = genPoly(n);
  const res = new Array(data.length + n).fill(0);
  for (let i = 0; i < data.length; i++) res[i] = data[i];
  for (let i = 0; i < data.length; i++) {
    const c = res[i];
    if (c === 0) continue;
    for (let j = 0; j < g.length; j++) res[i + j] ^= gmul(g[j], c);
  }
  return res.slice(data.length);
}

const QR_SPECS = [
  { v: 1, ec: 10, groups: [[1, 16]] },
  { v: 2, ec: 16, groups: [[1, 28]] },
  { v: 3, ec: 26, groups: [[1, 44]] },
  { v: 4, ec: 18, groups: [[2, 32]] },
  { v: 5, ec: 24, groups: [[2, 43]] },
  { v: 6, ec: 16, groups: [[4, 27]] },
];

const ALIGN_CENTERS = { 1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34] };

function buildMatrix(version, codewords) {
  const size = version * 4 + 17;
  const m = Array.from({ length: size }, () => new Array(size).fill(null));
  const fixed = Array.from({ length: size }, () => new Array(size).fill(false));

  const put = (r, c, v) => {
    if (r < 0 || c < 0 || r >= size || c >= size) return;
    m[r][c] = v;
    fixed[r][c] = true;
  };

  const finder = (r0, c0) => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const inside = r >= 0 && r <= 6 && c >= 0 && c <= 6;
        const dark =
          inside && (r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4));
        put(r0 + r, c0 + c, dark ? 1 : 0);
      }
    }
  };
  finder(0, 0);
  finder(0, size - 7);
  finder(size - 7, 0);

  for (let i = 8; i < size - 8; i++) {
    const v = i % 2 === 0 ? 1 : 0;
    if (m[6][i] === null) put(6, i, v);
    if (m[i][6] === null) put(i, 6, v);
  }

  const centers = ALIGN_CENTERS[version];
  const last = centers[centers.length - 1];
  for (const r of centers) {
    for (const c of centers) {
      if ((r === 6 && c === 6) || (r === 6 && c === last) || (r === last && c === 6)) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          put(r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1 ? 1 : 0);
        }
      }
    }
  }

  for (let i = 0; i < 9; i++) {
    if (m[8][i] === null) put(8, i, 0);
    if (m[i][8] === null) put(i, 8, 0);
  }
  for (let i = 0; i < 8; i++) {
    if (m[8][size - 1 - i] === null) put(8, size - 1 - i, 0);
    if (m[size - 1 - i][8] === null) put(size - 1 - i, 8, 0);
  }
  put(size - 8, 8, 1);

  const totalBits = codewords.length * 8;
  const bitAt = (i) => (codewords[i >> 3] >> (7 - (i & 7))) & 1;
  let bi = 0;
  let up = true;
  for (let col = size - 1; col > 0; col -= 2) {
    const c = col <= 6 ? col - 1 : col;
    for (let n = 0; n < size; n++) {
      const row = up ? size - 1 - n : n;
      for (const cc of [c, c - 1]) {
        if (m[row][cc] === null) {
          m[row][cc] = bi < totalBits ? bitAt(bi) : 0;
          bi++;
        }
      }
    }
    up = !up;
  }

  for (let r = 0; r < size; r++)
    for (let c = 0; c < size; c++) if (!fixed[r][c] && (r + c) % 2 === 0) m[r][c] ^= 1;

  // Format information: EC level M with mask 0. Written MSB first — bit 14
  // lands at (8,0). Getting this backwards makes a code that looks perfect
  // and that no scanner will touch, because the format word fails its BCH
  // check before the decoder ever reads the payload.
  const fmt = 0x5412;
  for (let i = 0; i < 15; i++) {
    const bit = (fmt >> (14 - i)) & 1;

    // Copy 1, wrapping the top-left finder. Skips (8,6) and (6,8), which
    // belong to the timing patterns.
    if (i < 6) m[8][i] = bit;
    else if (i === 6) m[8][7] = bit;
    else if (i === 7) m[8][8] = bit;
    else if (i === 8) m[7][8] = bit;
    else m[14 - i][8] = bit;

    // Copy 2: seven modules up column 8 from the bottom, then eight along
    // row 8 at the right. Seven, not eight — (size-8, 8) is the dark module
    // and must stay dark.
    if (i < 7) m[size - 1 - i][8] = bit;
    else m[8][size - 15 + i] = bit;
  }
  return m;
}

function qrMatrix(text) {
  const bytes = new TextEncoder().encode(text);
  const needed = 12 + bytes.length * 8;
  const spec = QR_SPECS.find((s) => s.groups.reduce((a, [n, d]) => a + n * d, 0) * 8 >= needed);
  if (!spec) return null;

  const cap = spec.groups.reduce((a, [n, d]) => a + n * d, 0) * 8;
  const bits = [];
  const push = (v, l) => {
    for (let i = l - 1; i >= 0; i--) bits.push((v >> i) & 1);
  };
  push(0b0100, 4);
  push(bytes.length, 8);
  bytes.forEach((b) => push(b, 8));
  for (let i = 0; i < 4 && bits.length < cap; i++) bits.push(0);
  while (bits.length % 8) bits.push(0);
  let pi = 0;
  while (bits.length < cap) push([0xec, 0x11][pi++ % 2], 8);

  const data = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    data.push(b);
  }

  const blocks = [];
  let p = 0;
  for (const [n, d] of spec.groups)
    for (let k = 0; k < n; k++) {
      blocks.push(data.slice(p, p + d));
      p += d;
    }
  const eccs = blocks.map((b) => reedSolomon(b, spec.ec));

  const out = [];
  const maxD = Math.max(...blocks.map((b) => b.length));
  for (let i = 0; i < maxD; i++) for (const b of blocks) if (i < b.length) out.push(b[i]);
  for (let i = 0; i < spec.ec; i++) for (const e of eccs) out.push(e[i]);

  return buildMatrix(spec.v, out);
}

// How wide the encoded symbol is, in modules, including `quiet` modules of
// margin on each side. Null when the value will not fit in the versions this
// encoder covers.
function qrModules(text, quiet = 4) {
  const m = qrMatrix(text);
  return m ? m.length + quiet * 2 : null;
}

/* `size` is a number of CSS pixels, or any CSS length string ("16.9mm") so a
   tape label can ask for a physical size rather than a screen one.

   `quiet` is how much of the mandated 4-module margin the symbol carries
   itself. Lowering it only makes sense where something else supplies the rest
   as real white — on tape that is the unprintable edge plus the label's own
   padding; see TapeStrip. Anywhere else, leave it alone. */
function QrCode({ text, size = 160, quiet = 4 }) {
  const path = useMemo(() => {
    const m = qrMatrix(text);
    if (!m) return null;
    const n = m.length;
    let d = "";
    for (let r = 0; r < n; r++)
      for (let c = 0; c < n; c++) if (m[r][c]) d += `M${c + quiet} ${r + quiet}h1v1h-1z`;
    return { d, dim: n + quiet * 2 };
  }, [text, quiet]);

  if (!path) return <div className="ai-qr-fail">Value too long to encode</div>;

  const dim = typeof size === "number" ? `${size}px` : size;

  return (
    <svg
      style={{ width: dim, height: dim, flex: "0 0 auto" }}
      viewBox={`0 0 ${path.dim} ${path.dim}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`QR code for ${text}`}
    >
      <rect width={path.dim} height={path.dim} fill="#fff" />
      <path d={path.d} fill="#16202B" />
    </svg>
  );
}

function PropertyTag({ tag, size = 128, className = "" }) {
  return (
    <div className={"ai-plate " + className}>
      <div className="ai-plate-eyebrow">{ORG_NAME}</div>
      <QrCode text={ASSET_URL_BASE + tag} size={size} />
      <div className="ai-plate-tag">{tag}</div>
      <div className="ai-plate-foot">Property tag · do not remove</div>
    </div>
  );
}

/* ==========================================================================
   1b. TAPE LABELS — what actually goes to the PT-P750W
   ========================================================================== */

/* What can go in the QR. Both scan: parseScan() strips the "/a/" prefix when
   there is one and otherwise takes the string as the tag, so a bare tag works
   in this app today. The difference is off-app — a stranger's phone camera
   opens the record from the URL and shows plain text from the tag — against
   size, and on tape size is the scarce thing. */
const QR_PAYLOADS = [
  { id: "auto", name: "Auto — keep the URL while it still prints" },
  { id: "url", name: "Asset URL — a phone camera opens the record" },
  { id: "tag", name: "Tag only — smallest, densest symbol" },
];

/* The symbol carries 2 of its 4 mandated quiet modules; the label supplies the
   rest as real white. Along the tape that is the computed padding and gap
   below. Across it, the tape's own unprintable edge — 1.05 mm on the narrowest
   supported width, several modules' worth — sits directly against the printed
   area. Height is the binding constraint on tape, so the two modules this
   saves on each side are worth having. */
const TAPE_QUIET = 2;

// Named because the width fitter has to measure it before it is rendered.
const TAPE_FOOT_TEXT = 'Property tag · do not remove';

/* Can this tape carry this payload, and how well? The symbol is square, so its
   size is bounded by the printable height, and that axis is stuck at 180 dpi
   (see TAPE_SPECS). Under ~3 head dots per module the printer starts merging
   modules and readers get unreliable; under ~2.2 it is hopeless. */
function qrFit(spec, text) {
  const modules = qrModules(text, TAPE_QUIET);
  if (!modules) return null;

  const box = spec.print - TAPE_PAD_MM * 2;
  const moduleMm = box / modules;
  const dots = moduleMm / DOT_MM;

  return {
    text,
    modules,
    box,
    moduleMm,
    dots,
    quality: dots >= 3 ? "good" : dots >= 2.2 ? "marginal" : "unusable",
  };
}

/* Resolves tape + payload mode into the one label this asset gets. When even
   the bare tag will not resolve, the label drops the QR and gives the whole
   height to the tag text — an unscannable smudge is worse than honest text. */
function tapeQrPlan(spec, tag, mode = "auto") {
  const url = qrFit(spec, ASSET_URL_BASE + tag);
  const bare = qrFit(spec, tag);

  const kind = mode === "auto" ? (url && url.quality === "good" ? "url" : "tag") : mode;
  const fit = kind === "url" ? url : bare;

  return {
    kind,
    fit,
    url,
    bare,
    withQr: !!fit && fit.quality !== "unusable",
    // auto traded the URL away to keep the symbol readable
    downgraded: mode === "auto" && kind === "tag" && !!url,
  };
}

/* Label geometry in one place, because it is needed twice: to render the strip,
   and to tell the operator what the strip will look like before they print 345
   of them. Duplicating this is how a preview and a print drift apart. */
function tapeLayout(spec, tag, payload = 'auto') {
  const plan = tapeQrPlan(spec, tag, payload);
  const h = spec.print;
  const withOrg = h >= 5;

  const flank = plan.withQr ? Math.max(TAPE_PAD_MM, plan.fit.moduleMm * TAPE_QUIET) : TAPE_PAD_MM;
  const gap = plan.withQr ? Math.max(TAPE_GAP_MM, plan.fit.moduleMm * TAPE_QUIET) : 0;
  const textWidthMm = spec.length - flank * 2 - (plan.withQr ? plan.fit.box : 0) - gap;

  // Monospace advance plus the letter-spacing the CSS applies to every glyph.
  const lineWidth = (text, sizeMm, lsEm) => String(text ?? '').length * (0.6 + lsEm) * sizeMm;
  const fitToWidth = (text, desiredMm, lsEm) => {
    const chars = String(text ?? '').length;
    if (!chars || textWidthMm <= 0) return desiredMm;
    return Math.min(desiredMm, textWidthMm / (chars * (0.6 + lsEm)));
  };

  const desiredTagMm = h * (plan.withQr ? 0.3 : withOrg ? 0.52 : 0.62);
  const tagMm = fitToWidth(tag, desiredTagMm, 0.1);

  /* What length would print the tag at its intended size? Offered rather than
     imposed — a longer label costs tape and a reduced tag is still legible, but
     the operator should make that trade knowingly rather than discover it on a
     printed label. */
  const lengthForFullSize = Math.ceil(
    flank * 2 + (plan.withQr ? plan.fit.box : 0) + gap + lineWidth(tag, desiredTagMm, 0.1),
  );

  return {
    plan,
    withOrg,
    flank,
    gap,
    textWidthMm,
    tagMm,
    desiredTagMm,
    shrunk: tagMm < desiredTagMm - 0.001,
    lengthForFullSize,
    orgMm: fitToWidth(ORG_NAME, h * 0.1, 0.1),
    footMm: fitToWidth(TAPE_FOOT_TEXT, h * 0.085, 0.08),
  };
}

/* The label as a person reads it: a strip `length` long and `print` tall, text
   running along the tape. This is the shape used on screen. It is NOT the shape
   sent to the printer — see TapePage.

   Sizes are in millimetres because the tape is: nothing here scales with the
   viewport, on screen or on the printer. */
function TapeStrip({ tag, spec, payload = "auto" }) {
  const L = tapeLayout(spec, tag, payload);
  const { plan } = L;

  return (
    <div
      className="ai-tape"
      style={{
        width: `${spec.length}mm`,
        height: `${spec.print}mm`,
        padding: `${TAPE_PAD_MM}mm ${L.flank}mm`,
      }}
    >
      {plan.withQr && (
        <QrCode text={plan.fit.text} size={`${plan.fit.box}mm`} quiet={TAPE_QUIET} />
      )}
      <div className="ai-tape-text" style={{ marginLeft: `${L.gap}mm` }}>
        {L.withOrg && (
          <div className="ai-tape-org" style={{ fontSize: `${L.orgMm}mm` }}>{ORG_NAME}</div>
        )}
        <div className="ai-tape-tag" style={{ fontSize: `${L.tagMm}mm` }}>{tag}</div>
        {plan.withQr && (
          <div className="ai-tape-foot" style={{ fontSize: `${L.footMm}mm` }}>
            {TAPE_FOOT_TEXT}
          </div>
        )}
      </div>
    </div>
  );
}

/* One printed page, in the printer's own terms rather than the reader's.

   P-touch drivers — the Linux ptouch PPD and Brother's Windows driver alike —
   define the page as *across* the tape by *along* it: the Linux `tz-12` size is
   11.6 × 100.2 mm, and Brother's own asset-tag guidance says paper 0.94" (=
   24 mm, the full tape width) with the length set separately. So the page is
   portrait, its width is the whole tape width — margins are the driver's job,
   not ours — and the readable strip is rotated into it here.

   Rotating in CSS rather than asking the driver to do it is the entire point.
   Handing the driver a landscape page and `-o landscape` was measured on a real
   PT-P750W: the flag was ignored, the 30 mm-wide page was scaled down to the
   12 mm paper width — a 0.4x shrink that destroyed the QR — and the overflow
   spilled onto a second blank label. Matching the paper exactly leaves nothing
   to scale and nothing to paginate. */
const TAPE_ROTATE_DEG = 90;

function TapePage({ tag, spec, payload = "auto" }) {
  return (
    <div className="ai-tape-page" style={{ width: `${spec.tape}mm`, height: `${spec.length}mm` }}>
      <div
        className="ai-tape-rot"
        style={{
          width: `${spec.length}mm`,
          height: `${spec.print}mm`,
          transform: `translate(-50%, -50%) rotate(${TAPE_ROTATE_DEG}deg)`,
        }}
      >
        <TapeStrip tag={tag} spec={spec} payload={payload} />
      </div>
    </div>
  );
}

/* The only thing the printer ever sees. Hidden on screen, and the sole visible
   block once `.is-printing-labels` is on the root, so the surrounding app
   cannot leak a stray page into the middle of a tape run.

   The @page rule has to be built as text: no browser resolves custom
   properties inside `size`, so the tape geometry cannot come from a variable. */
function TapePrintLayer({ job }) {
  if (!job) return null;
  const { tags, spec, payload } = job;
  return (
    <>
      <style>{`@page { size: ${spec.tape}mm ${spec.length}mm; margin: 0; }`}</style>
      <div className="ai-tape-print">
        {tags.map((tag) => (
          <TapePage key={tag} tag={tag} spec={spec} payload={payload} />
        ))}
      </div>
    </>
  );
}

/* ── Printing ──────────────────────────────────────────────────────────────
   Held at the root because printing is a whole-document act: the register
   screen and the label sheet both hand tags to the same queue rather than
   each calling window.print() on whatever happens to be rendered.           */
const LabelPrinter = createContext({
  spec: TAPE_SPECS[0],
  payload: "auto",
  setTape: () => {},
  setLength: () => {},
  setPayload: () => {},
  print: () => {},
});
const useLabelPrinter = () => useContext(LabelPrinter);

/* ==========================================================================
   2. REFERENCE DATA
   ========================================================================== */

// Statuses, in the order the selects should offer them. The labels come from
// api.js so this list cannot drift from the database enums it mirrors — the
// codes are authoritative and these are only what a person reads.
const ITEM_STATUS = ["available", "assigned", "in_repair", "awaiting_return", "retired", "lost"].map(
  statusLabel,
);
const STAFF_STATUS = ["active", "on_leave", "notice", "offboarded"].map(staffStatusLabel);

// Employees a person can no longer be trusted to keep holding equipment.
const AT_RISK = [staffStatusLabel("notice"), staffStatusLabel("offboarded")];

// Label → code, for turning a select's value back into something the API takes.
const STATUS_CODES = Object.fromEntries(Object.entries(STATUS_LABELS).map(([c, l]) => [l, c]));
const STAFF_CODES = Object.fromEntries(Object.entries(STAFF_STATUS_LABELS).map(([c, l]) => [l, c]));

/* ── Reference data ────────────────────────────────────────────────────────
   Departments and categories are rows in the database, not constants — a new
   cost centre should not need a redeploy. GET /api/reference supplies them, and
   this context carries them to the forms that render the selects. */

const Reference = createContext({
  departments: [], categories: [], categoryCode: () => null, stockDept: null,
});

const useReference = () => useContext(Reference);

/* ── ADAPTERS ──────────────────────────────────────────────────────────────
   The single translation point between the database's vocabulary and the
   screens'. If a field looks wrong on screen, it is wrong here. */

// The API returns `date` columns as plain 'YYYY-MM-DD' strings (see the type
// parser in server/db.js) and timestamptz as ISO instants. Both are safe to
// hand to fmtDate/fmtDateTime.
const toAsset = (a) => ({
  tag: a.tag,
  name: a.description,
  category: a.category_name,
  categoryCode: a.category_code,
  serial: a.serial_no ?? "",
  status: statusLabel(a.status),
  holder: a.holder_id,
  since: a.held_since,
  department: a.department_code,
  acquired: a.acquired_date,
});

const toPerson = (p) => ({
  id: p.id,
  name: p.name,
  mail: p.email,
  dept: p.department_code ?? "—",
  title: p.job_title ?? "—",
  status: staffStatusLabel(p.status),
  exitDate: p.exit_date,
  role: p.app_role,
});

const toEvent = (e) => ({
  id: e.event_id,
  tag: e.tag,
  type: eventLabel(e.event_type),
  at: e.occurred_at,
  by: e.actor_name ?? "—",
  note: e.note,
});


/* ==========================================================================
   3. HELPERS
   ========================================================================== */

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

const nowIso = () => new Date().toISOString();

const initials = (name) =>
  name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const itemClass = (s) =>
  ({ Available: "ok", Assigned: "out", "In repair": "warn", "Awaiting return": "warn", Retired: "dead", Lost: "bad" }[s] || "dead");

const staffClass = (s) =>
  ({ Active: "ok", "On leave": "out", "Notice period": "warn", Offboarded: "bad" }[s] || "dead");

const daysSince = (iso) => Math.floor((Date.now() - new Date(iso)) / 86400000);

function parseScan(raw) {
  const t = raw.trim();
  if (!t) return null;
  const fromUrl = t.match(/\/a\/([A-Za-z0-9-]+)\s*$/);
  return (fromUrl ? fromUrl[1] : t).toUpperCase();
}


/* ==========================================================================
   4. SMALL SHARED PIECES
   ========================================================================== */

function Avatar({ name, size = 32 }) {
  return (
    <span className="ai-avatar" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {initials(name)}
    </span>
  );
}

function Chip({ status, kind = "item" }) {
  const cls = kind === "staff" ? staffClass(status) : itemClass(status);
  return <span className={"ai-chip ai-chip-" + cls}>{status}</span>;
}

// Exported so the sign-in page carries the same control — someone who prefers a
// dark screen wants it before they are signed in, not after.
export function ThemeToggle({ className = "" }) {
  const { theme, isExplicit, toggle, useSystem } = useTheme();
  const goingTo = theme === "dark" ? "light" : "dark";
  return (
    <button
      className={"ai-btn ai-btn-quiet ai-btn-sm ai-theme-btn " + className}
      onClick={toggle}
      // Double-click hands control back to the OS. Deliberately not a visible
      // third button: it is a recovery path, not a daily choice.
      onDoubleClick={useSystem}
      aria-label={`Switch to ${goingTo} mode`}
      title={
        isExplicit
          ? `${theme === "dark" ? "Night" : "Day"} mode — double-click to follow your system`
          : `Following your system (${theme})`
      }
    >
      <span aria-hidden="true">{theme === "dark" ? "☾" : "☀"}</span>
      {!isExplicit && <span className="ai-theme-auto" aria-hidden="true">auto</span>}
    </button>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="ai-field">
      <dt>{label}</dt>
      <dd className={mono ? "is-mono" : ""}>{value}</dd>
    </div>
  );
}

/* ==========================================================================
   6. OVERVIEW — recovery queue is the point of this screen
   ========================================================================== */

function Overview({ assets, employees, events, onOpenAsset, onOpenPerson }) {
  const { departments } = useReference();
  const byId = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

  const recovery = useMemo(() => {
    const groups = new Map();
    for (const a of assets) {
      if (!a.holder) continue;
      const p = byId[a.holder];
      if (!p || !AT_RISK.includes(p.status)) continue;
      if (!groups.has(p.id)) groups.set(p.id, { person: p, items: [] });
      groups.get(p.id).items.push(a);
    }
    return [...groups.values()].sort((x, y) => {
      const rank = (s) => (s === "Offboarded" ? 0 : 1);
      return rank(x.person.status) - rank(y.person.status);
    });
  }, [assets, byId]);

  const stats = {
    total: assets.length,
    assigned: assets.filter((a) => a.status === "Assigned").length,
    available: assets.filter((a) => a.status === "Available").length,
    attention: assets.filter((a) => ["In repair", "Lost", "Awaiting return"].includes(a.status)).length,
  };

  const recent = [...events].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 7);
  const atRisk = recovery.reduce((s, g) => s + g.items.length, 0);

  return (
    <div className="ai-overview">
      <div className="ai-stats">
        <Stat label="Registered" value={stats.total} />
        <Stat label="Assigned" value={stats.assigned} />
        <Stat label="Available" value={stats.available} />
        <Stat label="Needs attention" value={stats.attention} />
      </div>

      <section className="ai-panel ai-panel-alert">
        <header className="ai-panel-head">
          <div>
            <div className="ai-eyebrow">Recovery queue</div>
            <h2 className="ai-panel-title">
              {recovery.length === 0
                ? "Nothing outstanding"
                : `${recovery.length} ${recovery.length === 1 ? "person" : "people"} leaving with equipment`}
            </h2>
          </div>
          {atRisk > 0 && (
            <div className="ai-exposure">
              <span className="ai-eyebrow">Items to recover</span>
              <span className="ai-exposure-value">{atRisk}</span>
            </div>
          )}
        </header>

        {recovery.length === 0 ? (
          <p className="ai-panel-empty">
            Everyone holding equipment is currently active. This list fills automatically when an
            Entra account is disabled or set to notice period.
          </p>
        ) : (
          <ul className="ai-recovery">
            {recovery.map(({ person, items }) => (
              <li key={person.id}>
                <button className="ai-recovery-person" onClick={() => onOpenPerson(person.id)}>
                  <Avatar name={person.name} size={34} />
                  <span className="ai-recovery-meta">
                    <span className="ai-recovery-name">{person.name}</span>
                    <span className="ai-recovery-sub">
                      {person.dept} · last day {fmtDate(person.exitDate)}
                    </span>
                  </span>
                  <Chip status={person.status} kind="staff" />
                </button>
                <div className="ai-recovery-items">
                  {items.map((a) => (
                    <button key={a.tag} className="ai-recovery-item" onClick={() => onOpenAsset(a.tag)}>
                      <span className="ai-mono-tag">{a.tag}</span>
                      <span className="ai-recovery-item-name">{a.name}</span>
                      <span className="ai-dept">{a.department}</span>
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <DepartmentTable assets={assets} departments={departments} />

      <section className="ai-panel">
        <div className="ai-eyebrow">Recent activity</div>
        <ul className="ai-feed">
          {recent.map((e, i) => (
            <li key={i}>
              <button className="ai-feed-row" onClick={() => onOpenAsset(e.tag)}>
                <span className="ai-mono-tag">{e.tag}</span>
                <span className="ai-feed-type">{e.type}</span>
                <span className="ai-feed-note">{e.note}</span>
                <time className="ai-feed-time">{fmtDateTime(e.at)}</time>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="ai-stat">
      <span className="ai-stat-label">{label}</span>
      <span className="ai-stat-value">{value}</span>
    </div>
  );
}

/* ==========================================================================
   7. OWNERSHIP CARD — what a scan is really for
   ========================================================================== */

function OwnershipCard({ asset, holder, onOpenPerson }) {
  if (!asset.holder) {
    return (
      <div className="ai-own ai-own-none">
        <div className="ai-eyebrow">Holder</div>
        <p className="ai-own-empty">
          Not assigned to anyone. Status is <strong>{asset.status}</strong>, held under {asset.department}.
        </p>
      </div>
    );
  }

  const risky = AT_RISK.includes(holder.status);

  return (
    <div className={"ai-own" + (risky ? " is-risky" : "")}>
      <div className="ai-eyebrow">Held by</div>
      <button className="ai-own-person" onClick={() => onOpenPerson(holder.id)}>
        <Avatar name={holder.name} size={44} />
        <span className="ai-own-meta">
          <span className="ai-own-name">{holder.name}</span>
          <span className="ai-own-sub">
            {holder.title} · {holder.dept}
          </span>
          <span className="ai-own-mail">{holder.mail}</span>
        </span>
      </button>

      <div className="ai-own-facts">
        <Chip status={holder.status} kind="staff" />
        <span className="ai-own-fact">
          Held {daysSince(asset.since)} days, since {fmtDate(asset.since)}
        </span>
      </div>

      {risky && (
        <p className="ai-own-warn">
          {holder.status === "Offboarded"
            ? `This person left on ${fmtDate(holder.exitDate)} and still has this device. Recover it.`
            : `Last day is ${fmtDate(holder.exitDate)}. Arrange collection before then.`}
        </p>
      )}
    </div>
  );
}

/* ==========================================================================
   8. SCAN
   ========================================================================== */

function ScanView({ assets, employees, onOpenAsset, onOpenPerson, onAction, user }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [log, setLog] = useState([]);
  const [camera, setCamera] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => inputRef.current?.focus(), []);

  const live = result ? assets.find((a) => a.tag === result) : null;
  const holder = live?.holder ? employees.find((e) => e.id === live.holder) : null;
  const can = (p) => hasPermission(user, p);

  const submit = (raw) => {
    const tag = parseScan(raw);
    if (!tag) return;
    const hit = assets.find((a) => a.tag === tag);
    if (!hit) {
      setError(`No asset registered as ${tag}. Check the printed tag, or register it from the Assets tab.`);
      setResult(null);
      return;
    }
    setError("");
    setValue("");
    setResult(hit.tag);
    setLog((prev) => [{ tag: hit.tag, name: hit.name, at: nowIso() }, ...prev].slice(0, 10));
  };

  const simulate = () => {
    const a = assets[Math.floor(Math.random() * assets.length)];
    const url = ASSET_URL_BASE + a.tag;
    setValue(url);
    setTimeout(() => submit(url), 300);
  };

  return (
    <div className="ai-scan">
      <div className="ai-scan-main">
        <div className="ai-panel">
          <div className="ai-eyebrow">Scanner input</div>
          <p className="ai-scan-help">
            A hardware scanner types the tag and presses Enter. To test the real path, point your phone
            camera at any tag in this app and paste the URL here.
          </p>
          {camera && (
            <div className="ai-camera">
              <TagScanner onScan={(raw) => submit(raw)} onClose={() => setCamera(false)} />
            </div>
          )}

          <input
            ref={inputRef}
            className="ai-input ai-input-lg"
            value={value}
            placeholder="AST-0107"
            onChange={(e) => {
              setValue(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && submit(value)}
          />
          <div className="ai-action-row">
            <button className="ai-btn ai-btn-primary" onClick={() => submit(value)} disabled={!value.trim()}>
              Look up
            </button>
            <button className="ai-btn" onClick={() => setCamera((c) => !c)}>
              {camera ? "Stop camera" : "Use camera"}
            </button>
            <button className="ai-btn ai-btn-quiet" onClick={simulate}>
              Simulate a scan
            </button>
          </div>
          {error && <p className="ai-error">{error}</p>}
          <p className="ai-note">
            [WIRE UP] Swap this input for <code>html5-qrcode</code> or the native{" "}
            <code>BarcodeDetector</code> API. Camera access requires HTTPS, including locally.
          </p>
        </div>

        {live && (
          <div className="ai-panel ai-result">
            <header className="ai-result-head">
              <div>
                <div className="ai-mono-tag ai-result-tag">{live.tag}</div>
                <h2 className="ai-result-name">{live.name}</h2>
                <div className="ai-result-sub">
                  {live.category} · {live.serial} · {live.department}
                </div>
              </div>
              <Chip status={live.status} />
            </header>

            <OwnershipCard asset={live} holder={holder} onOpenPerson={onOpenPerson} />

            <div className="ai-action-row ai-result-actions">
              {live.holder && can("assign") && (
                <button className="ai-btn ai-btn-primary" onClick={() => onAction(live.tag, "return", {})}>
                  Take back
                </button>
              )}
              {can("status") && (
                <button className="ai-btn" onClick={() => onAction(live.tag, "audit", {})}>
                  Confirm present
                </button>
              )}
              <button className="ai-btn ai-btn-quiet" onClick={() => onOpenAsset(live.tag)}>
                Full record
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="ai-panel ai-scan-log">
        <div className="ai-eyebrow">This session</div>
        {log.length === 0 ? (
          <p className="ai-panel-empty">Scans you resolve will be listed here.</p>
        ) : (
          <ul className="ai-log">
            {log.map((l, i) => (
              <li key={i}>
                <button onClick={() => setResult(l.tag)}>
                  <span className="ai-mono-tag">{l.tag}</span>
                  <span className="ai-log-name">{l.name}</span>
                  <time className="ai-log-time">
                    {new Date(l.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                  </time>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


/* Equipment per department — deliberately one number.

   This started as Items / Assigned / Available / Other and was cut back twice.
   Those columns were both misleading (they did not sum, so repairs and losses
   vanished) and beside the point: the question people actually bring to this
   panel is "how much of each department's kit is out with someone".

   So one figure, with its denominator — "5 in use" is meaningless without
   knowing whether that is 5 of 5 or 5 of 51 — and the bar is the in-use share
   of that department's own holdings, so a short bar means equipment sitting in
   stock. Anything finer belongs in the Assets tab, which already filters by
   department and status.

   "Charged to" is still worth saying: an asset charged to ITSC and used by
   someone in BPC counts here under ITSC. */
function DepartmentTable({ assets, departments }) {
  const rows = departments
    .map(({ code }) => {
      const items = assets.filter((a) => a.department === code);
      return {
        code,
        count: items.length,
        inUse: items.filter((a) => a.status === statusLabel("assigned")).length,
      };
    })
    .filter((r) => r.count > 0)
    .sort((a, b) => b.inUse - a.inUse || b.count - a.count || a.code.localeCompare(b.code));

  const hidden = departments.length - rows.length;

  return (
    <section className="ai-panel">
      <div className="ai-eyebrow">In use by department</div>
      <p className="ai-note ai-note-inline">
        Assets charged to each department that are out with someone. Where an asset is charged is
        not always where its holder sits.
      </p>
      <table className="ai-deptable">
        <thead>
          <tr>
            <th>Dept</th>
            <th className="ai-num">In use</th>
            <th>Share of the department&rsquo;s own kit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code}>
              <td><span className="ai-dept">{r.code}</span></td>
              <td className="ai-num">
                {r.inUse} <span className="ai-of">of {r.count}</span>
              </td>
              <td className="ai-barcell">
                <span
                  className="ai-bar"
                  style={{ width: (r.count ? (r.inUse / r.count) * 100 : 0) + "%" }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 && (
        <p className="ai-note">
          {hidden} department{hidden === 1 ? "" : "s"} hold no equipment and{" "}
          {hidden === 1 ? "is" : "are"} not listed.
        </p>
      )}
    </section>
  );
}

/* ==========================================================================
   9. REGISTER ASSET — generates the tag and the printable QR
   ========================================================================== */

function RegisterForm({ onCreate, onCancel }) {
  const { departments, categories, stockDept } = useReference();
  const { spec, payload, print } = useLabelPrinter();
  /* New equipment lands in stock (ITSC) until somebody is issued it — that is
     the common case, and the server defaults the same way when no department is
     sent. Falling back to departments[0] would silently file a new laptop under
     whichever department sorts first. */
  const defaultDept =
    (stockDept && departments.some((d) => d.code === stockDept) && stockDept) ??
    departments[0]?.code ??
    "";
  const [form, setForm] = useState({
    name: "", category: categories[0]?.name ?? "", serial: "",
    department: defaultDept, acquired: new Date().toISOString().slice(0, 10),
  });
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && form.serial.trim() && form.category && form.department;

  // The tag is allocated by the database sequence, so it is not known until the
  // server answers. Nothing is printable before then — which is the point: the
  // old prototype guessed max(tag)+1 locally, and two people registering at the
  // same moment would print the same label twice.
  const submit = async () => {
    setSaving(true);
    try {
      const asset = await onCreate(form);
      if (asset) setCreated(asset);
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <div className="ai-panel ai-register">
        <div className="ai-eyebrow">Registered</div>
        <h2 className="ai-panel-title">{created.name}</h2>
        <p className="ai-register-done">
          Tag <strong>{created.tag}</strong> is live. Print the label below and stick it on the device
          before it leaves the room — an unlabelled asset is an untracked asset.
        </p>
        <div className="ai-register-tag">
          <PropertyTag tag={created.tag} size={140} />
          <div className="ai-register-strip">
            <div className="ai-eyebrow">On {spec.tape} mm tape · actual size</div>
            <TapeStrip tag={created.tag} spec={spec} payload={payload} />
          </div>
        </div>
        <div className="ai-action-row">
          <button className="ai-btn ai-btn-primary" onClick={() => print([created.tag])}>
            Print label
          </button>
          <button className="ai-btn ai-btn-quiet" onClick={onCancel}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-panel ai-register">
      <header className="ai-panel-head">
        <div>
          <div className="ai-eyebrow">Register asset</div>
          <h2 className="ai-panel-title">New asset</h2>
        </div>
        <button className="ai-icon-btn" onClick={onCancel} aria-label="Cancel">✕</button>
      </header>

      <div className="ai-form">
        <label className="ai-form-row">
          <span className="ai-field-label">Description</span>
          <input className="ai-input" value={form.name} onChange={set("name")} placeholder='MacBook Pro 14"' />
        </label>
        <label className="ai-form-row">
          <span className="ai-field-label">Serial number</span>
          <input className="ai-input" value={form.serial} onChange={set("serial")} placeholder="C02XL4RTQ" />
        </label>
        <label className="ai-form-row">
          <span className="ai-field-label">Category</span>
          <select className="ai-select" value={form.category} onChange={set("category")}>
            {categories.map((c) => <option key={c.code}>{c.name}</option>)}
          </select>
        </label>
        <label className="ai-form-row">
          <span className="ai-field-label">Department</span>
          <select className="ai-select" value={form.department} onChange={set("department")}>
            {departments.map((d) => <option key={d.code}>{d.code}</option>)}
          </select>
        </label>
        <label className="ai-form-row">
          <span className="ai-field-label">Acquired</span>
          <input className="ai-input" type="date" value={form.acquired} onChange={set("acquired")} />
        </label>
      </div>

      <div className="ai-action-row">
        <button className="ai-btn ai-btn-primary" disabled={!valid || saving} onClick={submit}>
          {saving ? "Registering…" : "Register and generate tag"}
        </button>
        <button className="ai-btn ai-btn-quiet" onClick={onCancel}>Cancel</button>
      </div>
      {!valid && <p className="ai-note">Description and serial number are required.</p>}
    </div>
  );
}

/* ==========================================================================
   10. ASSET DETAIL
   ========================================================================== */

function AssetDetail({ asset, employees, events, user, onClose, onAction, onOpenPerson }) {
  const { departments } = useReference();
  const [note, setNote] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [status, setStatus] = useState(asset.status);
  const [department, setDepartment] = useState(asset.department);

  useEffect(() => {
    setNote("");
    setStatus(asset.status);
    setDepartment(asset.department);
    setAssignTo("");
  }, [asset.tag, asset.status, asset.department]);

  const can = (p) => hasPermission(user, p);
  const holder = asset.holder ? employees.find((e) => e.id === asset.holder) : null;
  const assignable = employees.filter((e) => !AT_RISK.includes(e.status));
  const history = events.filter((e) => e.tag === asset.tag).sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <aside className="ai-detail">
      <header className="ai-detail-head">
        <div>
          <div className="ai-eyebrow">Asset record</div>
          <div className="ai-detail-tag">{asset.tag}</div>
        </div>
        <button className="ai-icon-btn" onClick={onClose} aria-label="Close asset record">✕</button>
      </header>

      <div className="ai-detail-scroll">
        <h2 className="ai-detail-name">{asset.name}</h2>
        <Chip status={asset.status} />

        <OwnershipCard asset={asset} holder={holder} onOpenPerson={onOpenPerson} />

        <div className="ai-detail-tagwrap">
          <PropertyTag tag={asset.tag} size={120} />
        </div>

        <dl className="ai-fields">
          <Field label="Serial" value={asset.serial} mono />
          <Field label="Category" value={asset.category} />
          <Field label="Department" value={asset.department} />
          <Field label="Acquired" value={fmtDate(asset.acquired)} />
          <Field label="Assigned since" value={fmtDate(asset.since)} />
        </dl>

        {can("assign") || can("status") ? (
          <section className="ai-actions">
            <div className="ai-eyebrow">Record activity</div>
            <label className="ai-field-label" htmlFor="ai-note">Note</label>
            <input
              id="ai-note"
              className="ai-input"
              value={note}
              placeholder="What happened, and why"
              onChange={(e) => setNote(e.target.value)}
            />

            {can("assign") && (
              <div className="ai-action-row">
                {asset.holder ? (
                  <button className="ai-btn ai-btn-primary ai-grow" onClick={() => onAction(asset.tag, "return", { note })}>
                    Take back from {holder?.name.split(" ")[0]}
                  </button>
                ) : (
                  <>
                    <select className="ai-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                      <option value="">Assign to…</option>
                      {assignable.map((e) => (
                        <option key={e.id} value={e.id}>{e.name} — {e.dept}</option>
                      ))}
                    </select>
                    <button
                      className="ai-btn ai-btn-primary"
                      disabled={!assignTo || ["Retired", "Lost"].includes(asset.status)}
                      onClick={() => onAction(asset.tag, "assign", { note, holder: assignTo })}
                    >
                      Assign
                    </button>
                  </>
                )}
              </div>
            )}

            {can("status") && (
              <>
                <div className="ai-action-row">
                  <select className="ai-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    {ITEM_STATUS.map((s) => (
                      <option key={s} disabled={s === statusLabel("assigned")}>{s}</option>
                    ))}
                  </select>
                  <button
                    className="ai-btn"
                    disabled={status === asset.status}
                    onClick={() => onAction(asset.tag, "status", { note, status })}
                  >
                    Set status
                  </button>
                </div>

                <div className="ai-action-row">
                  <select className="ai-select" value={department} onChange={(e) => setDepartment(e.target.value)}>
                    {departments.map((d) => <option key={d.code}>{d.code}</option>)}
                  </select>
                  <button
                    className="ai-btn"
                    disabled={department === asset.department}
                    onClick={() => onAction(asset.tag, "transfer", { note, department })}
                  >
                    Transfer
                  </button>
                </div>

                <button className="ai-btn ai-btn-quiet ai-full" onClick={() => onAction(asset.tag, "audit", { note })}>
                  Confirm present (audit scan)
                </button>
              </>
            )}
          </section>
        ) : (
          <p className="ai-readonly">
            Your role is read only. Ask an administrator for the Technician role to record activity.
          </p>
        )}

        <section className="ai-history">
          <div className="ai-eyebrow">History · {history.length} events</div>
          {history.length === 0 ? (
            <p className="ai-panel-empty">Nothing recorded against this asset yet.</p>
          ) : (
            <ol className="ai-timeline">
              {history.map((e, i) => (
                <li key={i}>
                  <div className="ai-tl-head">
                    <span className="ai-tl-type">{e.type}</span>
                    <time className="ai-tl-time">{fmtDateTime(e.at)}</time>
                  </div>
                  {e.note && <p className="ai-tl-note">{e.note}</p>}
                  <div className="ai-tl-by">{e.by}</div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </aside>
  );
}


/* Bulk import. Deliberately two steps: Check, then Import.

   The check is not a formality — inventory spreadsheets carry duplicate tags,
   Excel date serials and holder names that match nobody, and every one of those
   is invisible until something validates it. Importing is blocked while any row
   is broken, because a half-imported list cannot be told apart from a complete
   one a month later. */
function BulkImport({ onDone, onCancel }) {
  const { categories } = useReference();
  const [text, setText] = useState("");
  const [category, setCategory] = useState("LAPTOP");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async (dryRun) => {
    setBusy(true);
    setError(null);
    try {
      /* allowPartial on the real run: rows that fail on a holder who is not in
         the directory cannot be fixed in the spreadsheet, so blocking the whole
         file on them would mean importing nothing at all. Re-running later adds
         only what was missed, because the tags come from the file. */
      const r = await api.importAssets({
        text,
        defaultCategory: category,
        dryRun,
        allowPartial: !dryRun,
      });
      setResult(r);
      if (!dryRun) await onDone(r);
    } catch (err) {
      setError(err.message);
      if (dryRun) setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const problems = (result?.plan ?? []).filter((p) => (!p.ok && !p.skip) || p.warnings.length);
  const canImport = result && !result.created && result.ok > 0;
  const importLabel = !canImport
    ? "Import"
    : result.failed > 0
      ? `Import ${result.ok} of ${result.rows}`
      : `Import ${result.ok} asset${result.ok === 1 ? "" : "s"}`;

  return (
    <div className="ai-panel ai-register">
      <header className="ai-panel-head">
        <div>
          <div className="ai-eyebrow">Bulk import</div>
          <h2 className="ai-panel-title">Add assets from a list</h2>
        </div>
        <button className="ai-icon-btn" onClick={onCancel} aria-label="Cancel">✕</button>
      </header>

      <p className="ai-register-done">
        Paste the rows including the header, straight from Excel, a CSV, or a table in a
        document — tabs, commas and <code>|</code> all work. Columns are matched by name, so
        <strong> ASSET TAG NUMBER</strong>, <strong>LAPTOP MODEL</strong>, <strong>SERIAL NO.</strong>,
        <strong> STATUS</strong>, <strong>DEPT</strong>, <strong>CURRENT USER</strong> and
        <strong> DATE OF PURCHASE</strong> are picked up automatically. Anything else is ignored.
      </p>

      <label className="ai-form-row">
        <span className="ai-field-label">Category for every row</span>
        <select className="ai-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </label>

      <textarea
        className="ai-import-text"
        placeholder="LAPTOP MODEL	SERIAL NO.	ASSET TAG NUMBER	STATUS	…"
        value={text}
        onChange={(e) => { setText(e.target.value); setResult(null); }}
        spellCheck={false}
      />

      <div className="ai-action-row">
        <button className="ai-btn ai-btn-quiet" disabled={!text.trim() || busy} onClick={() => run(true)}>
          {busy ? "Checking…" : "Check"}
        </button>
        <button className="ai-btn ai-btn-primary" disabled={!canImport || busy} onClick={() => run(false)}>
          {busy ? "Importing…" : importLabel}
        </button>
        <button className="ai-btn ai-btn-quiet" onClick={onCancel}>Close</button>
      </div>

      {error && <p className="ai-note ai-note-inline is-warn">{error}</p>}

      {result && (
        <>
          <p className="ai-note ai-note-inline">
            {result.created != null
              ? `Imported ${result.created} assets.`
              : `${result.rows} rows read — ${result.ok} ready, ${result.failed} with errors.`}
            {result.skipped > 0 && ` ${result.skipped} already in the register, skipped.`}
            {result.assigned > 0 && ` ${result.assigned} assigned to someone.`}
            {result.ignoredColumns?.length > 0 &&
              ` Ignored columns: ${result.ignoredColumns.join(", ")}.`}
          </p>

          {result.created != null && result.notImported > 0 && (
            <p className="ai-note ai-note-inline is-warn">
              {result.notImported} row{result.notImported === 1 ? "" : "s"} could not be imported and
              are listed below. Fix them at source and paste the file again — rows already in the
              register are skipped, so nothing is duplicated.
            </p>
          )}

          {result.created == null && result.failed > 0 && (
            <p className="ai-note ai-note-inline is-warn">
              Importing will add the {result.ok} valid row{result.ok === 1 ? "" : "s"} and leave{" "}
              {result.failed} behind.
            </p>
          )}

          {problems.length > 0 && (
            <div className="ai-import-problems">
              {problems.map((p) => (
                <div key={p.line} className={"ai-import-row" + (p.ok ? "" : " is-bad")}>
                  <span className="ai-mono-tag">{p.tag ?? `line ${p.line}`}</span>
                  <div className="ai-import-msgs">
                    {p.errors.map((e, i) => <div key={"e" + i} className="is-warn">{e}</div>)}
                    {p.warnings.map((w, i) => <div key={"w" + i}>{w}</div>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* Receiving new equipment by scanning cartons.

   The register was assembled from a spreadsheet, but new stock arrives as a
   stack of boxes, and the serial on the carton is the field nobody wants to
   retype — twelve characters of PF5M36BQ-style noise, mistyped once, and the
   asset can never be matched to the machine again.

   So: set the fields every box in the batch shares (model, category,
   department, purchase date) once, then scan cartons one after another. Each
   scan stages a row rather than creating an asset immediately — a misread is
   easy to delete from a list and impossible to undo from a register, and the
   whole batch goes in through the bulk importer, which already refuses
   duplicate serials and writes the registered event.

   Tags are generated here rather than by the database sequence. The register
   uses SXICELT260042-style tags on 310 of its assets; next_asset_tag() would
   hand out AST-0158 and leave two schemes side by side on physical labels. The
   next number in the existing series is offered by default and can be
   overridden. */
function nextTagInSeries(assets) {
  // The dominant prefix wins: 310 SXICELT2…, 11 SXICELT1…, 2 SXICEDS2…
  const counts = {};
  for (const a of assets) {
    const m = String(a.tag).match(/^([A-Za-z]+)(\d+)$/);
    if (!m) continue;
    const [, prefix, digits] = m;
    counts[prefix] ??= { prefix, width: digits.length, max: 0, n: 0 };
    counts[prefix].n += 1;
    counts[prefix].max = Math.max(counts[prefix].max, Number(digits));
    counts[prefix].width = Math.max(counts[prefix].width, digits.length);
  }
  const best = Object.values(counts).sort((a, b) => b.n - a.n)[0];
  if (!best) return { prefix: "", start: "" };
  return { prefix: best.prefix, start: String(best.max + 1).padStart(best.width, "0") };
}

function ReceiveStock({ assets, onDone, onCancel }) {
  const { categories, departments, stockDept } = useReference();
  const series = useMemo(() => nextTagInSeries(assets), [assets]);

  const [form, setForm] = useState(() => ({
    description: "",
    category: categories[0]?.code ?? "LAPTOP",
    department: stockDept ?? departments[0]?.code ?? "",
    acquired: new Date().toISOString().slice(0, 10),
    tagPrefix: series.prefix,
    tagStart: series.start,
    useAppTag: false,
  }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [camera, setCamera] = useState(false);
  const [rows, setRows] = useState([]); // { serial, seen }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Serials already in the register: a repeat means the box was scanned twice
  // or the machine is already recorded, and either way it should not go in.
  const known = useMemo(
    () => new Set(assets.map((a) => String(a.serial ?? "").trim().toUpperCase()).filter(Boolean)),
    [assets],
  );

  const addSerial = (raw) => {
    const serial = String(raw ?? "").trim().toUpperCase();
    if (!serial) return;
    setRows((prev) =>
      prev.some((r) => r.serial === serial) ? prev : [...prev, { serial, seen: Date.now() }],
    );
  };

  const tagFor = (index) =>
    form.useAppTag || !form.tagPrefix
      ? ""
      : form.tagPrefix + String(Number(form.tagStart) + index).padStart(form.tagStart.length, "0");

  const problems = rows.map((r) => (known.has(r.serial) ? "already in the register" : null));
  const ready =
    rows.length > 0 && form.description.trim() && form.category && form.department &&
    problems.every((p) => !p);

  /* Handed to the bulk importer rather than POST /api/assets per row: it
     already validates, allocates or accepts tags, refuses duplicates and writes
     the registered event, and one transaction for the batch means a dropped
     connection cannot leave half a pallet recorded. */
  const submit = async () => {
    setBusy(true);
    setError(null);
    const header = ["ASSET TAG NUMBER", "LAPTOP MODEL", "SERIAL NO.", "STATUS", "DEPT", "DATE OF PURCHASE"];
    const body = rows.map((r, i) =>
      [tagFor(i), form.description.trim(), r.serial, "Spare", form.department, form.acquired].join("\t"),
    );
    try {
      const r = await api.importAssets({
        text: [header.join("\t"), ...body].join("\n"),
        defaultCategory: form.category,
        dryRun: false,
        allowPartial: false,
      });
      setResult(r);
      setRows([]);
      await onDone?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ai-panel ai-register">
      <header className="ai-panel-head">
        <div>
          <div className="ai-eyebrow">Receive stock</div>
          <h2 className="ai-panel-title">Scan cartons to register equipment</h2>
        </div>
        <button className="ai-icon-btn" onClick={onCancel} aria-label="Cancel">✕</button>
      </header>

      <p className="ai-register-done">
        Set what every box in this delivery has in common, then scan each carton&rsquo;s serial
        barcode. Scans are staged below — nothing is registered until you confirm.
      </p>

      <div className="ai-form">
        <label className="ai-form-row">
          <span className="ai-field-label">Model — the same for every box</span>
          <input className="ai-input" value={form.description} onChange={set("description")}
                 placeholder="LENOVO L14 GEN6" />
        </label>
        <label className="ai-form-row">
          <span className="ai-field-label">Category</span>
          <select className="ai-select" value={form.category} onChange={set("category")}>
            {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </label>
        <label className="ai-form-row">
          <span className="ai-field-label">Department</span>
          <select className="ai-select" value={form.department} onChange={set("department")}>
            {departments.map((d) => <option key={d.code} value={d.code}>{d.code}</option>)}
          </select>
        </label>
        <label className="ai-form-row">
          <span className="ai-field-label">Date of purchase</span>
          <input className="ai-input" type="date" value={form.acquired} onChange={set("acquired")} />
        </label>
      </div>

      <div className="ai-recv-tags">
        <label className="ai-pick">
          <input type="checkbox" checked={!form.useAppTag}
                 onChange={(e) => setForm((f) => ({ ...f, useAppTag: !e.target.checked }))} />
          <span>Continue the existing tag series</span>
        </label>
        {form.useAppTag ? (
          <span className="ai-note">The database will allocate AST-style tags instead.</span>
        ) : (
          <>
            <input className="ai-input ai-recv-prefix" value={form.tagPrefix}
                   onChange={set("tagPrefix")} placeholder="SXICELT" />
            <input className="ai-input ai-recv-start" value={form.tagStart}
                   onChange={set("tagStart")} inputMode="numeric" />
            <span className="ai-note">
              {rows.length > 0
                ? `${tagFor(0)} … ${tagFor(rows.length - 1)}`
                : `next would be ${tagFor(0) || "—"}`}
            </span>
          </>
        )}
      </div>

      <div className="ai-action-row">
        <button className="ai-btn ai-btn-quiet" onClick={() => setCamera((c) => !c)}>
          {camera ? "Stop camera" : "Scan cartons"}
        </button>
        <input
          className="ai-input"
          placeholder="…or type / wedge-scan a serial, then Enter"
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            addSerial(e.currentTarget.value);
            e.currentTarget.value = "";
          }}
        />
      </div>

      {camera && (
        <div className="ai-camera">
          {/* box mode: cartons carry Code 128 / Code 39, not QR */}
          <TagScanner mode="box" onScan={addSerial} onClose={() => setCamera(false)} />
        </div>
      )}

      {rows.length > 0 && (
        <div className="ai-import-problems">
          {rows.map((r, i) => (
            <div key={r.serial} className={"ai-import-row" + (problems[i] ? " is-bad" : "")}>
              <span className="ai-mono-tag">{tagFor(i) || "(auto)"}</span>
              <div className="ai-import-msgs">
                <div>{r.serial}</div>
                {problems[i] && <div className="is-warn">Serial {problems[i]}.</div>}
              </div>
              <button className="ai-icon-btn" aria-label={`Remove ${r.serial}`}
                      onClick={() => setRows((prev) => prev.filter((x) => x.serial !== r.serial))}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="ai-action-row">
        <button className="ai-btn ai-btn-primary" disabled={!ready || busy} onClick={submit}>
          {busy ? "Registering…" : rows.length ? `Register ${rows.length} asset${rows.length === 1 ? "" : "s"}` : "Register"}
        </button>
        <button className="ai-btn ai-btn-quiet" onClick={onCancel}>Close</button>
      </div>

      {error && <p className="ai-note ai-note-inline is-warn">{error}</p>}
      {result?.created > 0 && (
        <p className="ai-note ai-note-inline">
          Registered {result.created} asset{result.created === 1 ? "" : "s"}. Print their labels from
          the Labels tab.
        </p>
      )}
      {!ready && rows.length > 0 && problems.some(Boolean) && (
        <p className="ai-note ai-note-inline is-warn">
          Remove the serials already in the register before continuing.
        </p>
      )}
    </div>
  );
}

/* ==========================================================================
   11. ASSETS VIEW
   ========================================================================== */

function AssetsView({
  assets, employees, events, user, selectedTag, onSelect, onAction, onCreate,
  onOpenPerson, onReload, flash,
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [deptFilter, setDeptFilter] = useState("All");
  const [registering, setRegistering] = useState(false);
  const [importing, setImporting] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const { departments, categories } = useReference();

  const byId = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);
  const can = (p) => hasPermission(user, p);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return assets.filter((a) => {
      if (statusFilter !== "All" && a.status !== statusFilter) return false;
      if (categoryFilter !== "All" && a.category !== categoryFilter) return false;
      if (deptFilter !== "All" && a.department !== deptFilter) return false;
      if (!q) return true;
      const holderName = a.holder ? byId[a.holder]?.name || "" : "";
      return [a.tag, a.name, a.serial, a.department, holderName].join(" ").toLowerCase().includes(q);
    });
  }, [assets, query, statusFilter, categoryFilter, deptFilter, byId]);

  const selected = assets.find((a) => a.tag === selectedTag) || null;

  if (registering) {
    return (
      <RegisterForm onCreate={onCreate} onCancel={() => setRegistering(false)} />
    );
  }

  if (receiving) {
    return (
      <ReceiveStock
        assets={assets}
        onCancel={() => setReceiving(false)}
        onDone={async () => { await onReload?.(); }}
      />
    );
  }

  if (importing) {
    return (
      <BulkImport
        onCancel={() => setImporting(false)}
        onDone={async () => { await onReload?.(); }}
      />
    );
  }

  return (
    <>
      <div className="ai-filters">
        <input
          className="ai-input"
          placeholder="Search tag, description, serial, holder"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select className="ai-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option>All</option>
          {ITEM_STATUS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className="ai-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option>All</option>
          {categories.map((c) => <option key={c.code}>{c.name}</option>)}
        </select>
        <select className="ai-select" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
          <option>All</option>
          {departments.map((d) => <option key={d.code}>{d.code}</option>)}
        </select>
        {can("register") && (
          <>
            <button className="ai-btn ai-btn-quiet" onClick={() => setReceiving(true)}>
              Receive stock
            </button>
            <button className="ai-btn ai-btn-quiet" onClick={() => setImporting(true)}>
              Bulk import
            </button>
            <button className="ai-btn ai-btn-primary" onClick={() => setRegistering(true)}>
              Register asset
            </button>
          </>
        )}
      </div>

      <div className={"ai-split" + (selected ? " has-detail" : "")}>
        {filtered.length === 0 ? (
          <div className="ai-panel ai-empty">
            <p className="ai-empty-title">No assets match those filters.</p>
            <p className="ai-panel-empty">Clear the search box or widen the status filter.</p>
          </div>
        ) : (
          <div className="ai-table-wrap">
            <table className="ai-table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Asset</th>
                  <th>Held by</th>
                  <th>Dept</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const h = a.holder ? byId[a.holder] : null;
                  const risky = h && AT_RISK.includes(h.status);
                  return (
                    <tr
                      key={a.tag}
                      onClick={() => onSelect(a.tag)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelect(a.tag);
                        }
                      }}
                      className={(selectedTag === a.tag ? "is-selected" : "") + (flash === a.tag ? " is-flash" : "")}
                    >
                      <td className="ai-td-tag">{a.tag}</td>
                      <td>
                        <span className="ai-td-name">{a.name}</span>
                        <span className="ai-td-serial">{a.serial}</span>
                      </td>
                      <td>
                        {h ? (
                          <span className={"ai-td-holder" + (risky ? " is-risky" : "")}>
                            <Avatar name={h.name} size={22} />
                            {h.name}
                            {risky && <span className="ai-flag" title={h.status}>!</span>}
                          </span>
                        ) : (
                          <span className="ai-td-dim">—</span>
                        )}
                      </td>
                      <td><span className="ai-dept">{a.department}</span></td>
                      <td><Chip status={a.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selected && (
          <AssetDetail
            asset={selected}
            employees={employees}
            events={events}
            user={user}
            onClose={() => onSelect(null)}
            onAction={onAction}
            onOpenPerson={onOpenPerson}
          />
        )}
      </div>
    </>
  );
}

/* ==========================================================================
   12. PEOPLE VIEW
   ========================================================================== */

function PeopleView({
  employees, assets, user, selectedId, onSelect, onStaffStatus, onOpenAsset, onSynced,
}) {
  const [query, setQuery] = useState("");
  const [sync, setSync] = useState(null); // { busy } | { result } | { error }
  const can = (p) => hasPermission(user, p);

  /* The directory is the source of truth for who exists; this pulls it in so
     nobody retypes 300+ names. The server refuses to touch app_role or status,
     so a sync cannot grant access or reverse an offboarding. */
  const runSync = async () => {
    setSync({ busy: true });
    try {
      const result = await api.syncOrg();
      setSync({ result });
      await onSynced?.();
    } catch (err) {
      setSync({ error: err.message });
    }
  };

  const held = useMemo(() => {
    const m = {};
    for (const a of assets) if (a.holder) (m[a.holder] ||= []).push(a);
    return m;
  }, [assets]);

  const filtered = employees.filter((e) => {
    const q = query.trim().toLowerCase();
    return !q || [e.name, e.dept, e.title, e.mail].join(" ").toLowerCase().includes(q);
  });

  const person = employees.find((e) => e.id === selectedId) || null;
  const personAssets = person ? held[person.id] || [] : [];

  return (
    <>
      <div className="ai-filters ai-filters-people">
        <input
          className="ai-input"
          placeholder="Search name, department, role"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="ai-org-sync">
          <span className="ai-org-count">
            {employees.length} {employees.length === 1 ? "person" : "people"}
          </span>
          {can("settings") && (
            <button
              className="ai-btn ai-btn-quiet ai-btn-sm"
              disabled={sync?.busy}
              onClick={runSync}
            >
              {sync?.busy ? "Syncing…" : "Sync from Microsoft 365"}
            </button>
          )}
        </div>
      </div>

      {sync?.error && <p className="ai-note ai-note-inline is-warn">Sync failed: {sync.error}</p>}
      {sync?.result && (
        <p className="ai-note ai-note-inline">
          {sync.result.created} added, {sync.result.updated} updated,{" "}
          {sync.result.unchanged} already current. Skipped {sync.result.skipped.guests} guests,{" "}
          {sync.result.skipped.disabled} disabled and {sync.result.skipped.notAPerson} non-person
          accounts (rooms, devices, shared mailboxes).
          {sync.result.needsReview?.length > 0 &&
            ` ${sync.result.needsReview.length} imported account(s) have no department or job title — worth a look.`}
        </p>
      )}

      <div className={"ai-split" + (person ? " has-detail" : "")}>
        <div className="ai-people">
          {filtered.map((e) => {
            const items = held[e.id] || [];
            const risky = AT_RISK.includes(e.status) && items.length > 0;
            return (
              <button
                key={e.id}
                className={"ai-person" + (selectedId === e.id ? " is-selected" : "") + (risky ? " is-risky" : "")}
                onClick={() => onSelect(e.id)}
              >
                <Avatar name={e.name} size={38} />
                <span className="ai-person-meta">
                  <span className="ai-person-name">{e.name}</span>
                  <span className="ai-person-sub">{e.title} · {e.dept}</span>
                </span>
                <span className="ai-person-right">
                  <Chip status={e.status} kind="staff" />
                  <span className="ai-person-count">
                    {items.length} {items.length === 1 ? "item" : "items"}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {person && (
          <aside className="ai-detail">
            <header className="ai-detail-head">
              <div>
                <div className="ai-eyebrow">Employee</div>
                <div className="ai-detail-person">{person.name}</div>
              </div>
              <button className="ai-icon-btn" onClick={() => onSelect(null)} aria-label="Close">✕</button>
            </header>

            <div className="ai-detail-scroll">
              <div className="ai-person-hero">
                <Avatar name={person.name} size={52} />
                <div>
                  <div className="ai-own-sub">{person.title} · {person.dept}</div>
                  <div className="ai-own-mail">{person.mail}</div>
                </div>
              </div>

              <dl className="ai-fields">
                <Field label="Status" value={<Chip status={person.status} kind="staff" />} />
                <Field label="Last day" value={fmtDate(person.exitDate)} />
                <Field label="Items held" value={personAssets.length} mono />
                <Field label="Department" value={person.dept} />
              </dl>

              {can("staff") ? (
                <section className="ai-actions">
                  <div className="ai-eyebrow">Employment status</div>
                  <p className="ai-note">
                    Overrides the value derived from Entra. Setting notice period or offboarded moves
                    anything they hold into the recovery queue.
                  </p>
                  <div className="ai-seg ai-seg-wrap">
                    {STAFF_STATUS.map((s) => (
                      <button
                        key={s}
                        className={"ai-seg-btn" + (person.status === s ? " is-on" : "")}
                        onClick={() => onStaffStatus(person.id, s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </section>
              ) : (
                <p className="ai-readonly">Only administrators can change employment status.</p>
              )}

              <section className="ai-history">
                <div className="ai-eyebrow">Equipment held</div>
                {personAssets.length === 0 ? (
                  <p className="ai-panel-empty">Nothing assigned.</p>
                ) : (
                  <ul className="ai-held">
                    {personAssets.map((a) => (
                      <li key={a.tag}>
                        <button onClick={() => onOpenAsset(a.tag)}>
                          <span className="ai-mono-tag">{a.tag}</span>
                          <span className="ai-held-name">{a.name}</span>
                          <span className="ai-held-days">{daysSince(a.since)}d</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </aside>
        )}
      </div>
    </>
  );
}

/* ==========================================================================
   13. LABELS
   ========================================================================== */

/* What the tape and payload choice actually means for scanning, in numbers.
   "18 mm looked fine on screen" is exactly the mistake this exists to stop:
   the preview is true to size but a screen has no print head. */
function TapeAdvice({ spec, payload, sampleTag = "AI-0000" }) {
  const plan = tapeQrPlan(spec, sampleTag, payload);
  const layout = tapeLayout(spec, sampleTag, payload);

  if (!plan.fit) {
    return (
      <p className="ai-tape-note is-warn">
        Payload will not fit the QR versions this encoder supports — shorten ASSET_URL_BASE.
      </p>
    );
  }

  const { moduleMm, dots, quality } = plan.fit;
  const size = `${moduleMm.toFixed(2)} mm module (${dots.toFixed(1)} head dots)`;
  const what = plan.kind === "url" ? "asset URL" : "tag only";

  if (!plan.withQr) {
    return (
      <p className="ai-tape-note is-warn">
        {spec.tape} mm tape cannot resolve a QR — {size}. These labels print the tag as text only;
        use 18 mm or 24 mm tape if they need to be scannable.
      </p>
    );
  }

  return (
    <>
      <p className={"ai-tape-note" + (quality === "good" ? "" : " is-warn")}>
        QR carries the {what} · {size} —{" "}
        {quality === "good"
          ? "prints and scans cleanly."
          : "tight. Print one and scan it before committing a batch."}
        {plan.downgraded && " Auto dropped the URL to keep the symbol readable on this width."}
      </p>
      {layout.shrunk && (
        <p className="ai-tape-note">
          Tag text reduced to {layout.tagMm.toFixed(1)} mm (
          {Math.round((layout.tagMm / layout.desiredTagMm) * 100)}% of full size) so{" "}
          <strong>{sampleTag}</strong> fits. Still legible — a{" "}
          <strong>{layout.lengthForFullSize} mm</strong> label would print it full size, at the cost
          of more tape.
        </p>
      )}
    </>
  );
}

/* Clamping on every keystroke fights the typist — clearing the field to type a
   new number would snap it to the minimum mid-edit. So the draft is local and
   only committed once they are done with it. */
function TapeLengthInput({ length, onCommit }) {
  const [draft, setDraft] = useState(null);

  const commit = () => {
    if (draft !== null) onCommit(draft);
    setDraft(null);
  };

  return (
    <input
      type="number"
      min={TAPE_LENGTH_MIN}
      max={TAPE_LENGTH_MAX}
      step={1}
      value={draft ?? length}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && commit()}
    />
  );
}

function LabelSheet({ assets, selected, onToggle, onSelectAll, canPrint }) {
  const { spec, payload, setTape, setLength, setPayload, print } = useLabelPrinter();
  const chosen = assets.filter((a) => selected.has(a.tag));

  return (
    <div className="ai-labels">
      <div className="ai-labels-bar">
        <div>
          <div className="ai-eyebrow">Tape labels · Brother PT-P750W</div>
          <p className="ai-labels-count">{chosen.length} of {assets.length} selected</p>
        </div>
        <div className="ai-action-row">
          <button className="ai-btn ai-btn-quiet" onClick={() => onSelectAll(chosen.length !== assets.length)}>
            {chosen.length === assets.length ? "Clear all" : "Select all"}
          </button>
          <button
            className="ai-btn ai-btn-primary"
            disabled={!chosen.length || !canPrint}
            onClick={() => print(chosen.map((a) => a.tag))}
          >
            Print {chosen.length || ""} label{chosen.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>

      <div className="ai-tape-setup">
        <label className="ai-tape-ctl">
          <span className="ai-field-label">Tape width</span>
          <select value={spec.id} onChange={(e) => setTape(e.target.value)}>
            {TAPE_SPECS.map((s) => (
              <option key={s.id} value={s.id}>{s.tape} mm TZe · prints {s.print} mm</option>
            ))}
          </select>
        </label>
        <label className="ai-tape-ctl">
          <span className="ai-field-label">Label length (mm)</span>
          <TapeLengthInput length={spec.length} onCommit={setLength} />
        </label>
        <label className="ai-tape-ctl">
          <span className="ai-field-label">QR contains</span>
          <select value={payload} onChange={(e) => setPayload(e.target.value)}>
            {QR_PAYLOADS.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
        <div className="ai-tape-ctl">
          <span className="ai-field-label">Set driver paper to</span>
          <output className="ai-tape-page-size">
            {spec.tape} × {spec.length} mm
            <span className="ai-tape-inch"> ({inches(spec.tape)}" × {inches(spec.length)}")</span>
          </output>
        </div>
      </div>
      <TapeAdvice
        spec={spec}
        payload={payload}
        /* The longest selected tag decides the text size for the whole batch, so
           the advice should be about that rather than a typical one. */
        sampleTag={
          chosen.reduce((longest, a) => (a.tag.length > longest.length ? a.tag : longest), "") ||
          "AI-0000"
        }
      />
      <p className="ai-tape-note">
        One label per page, cut between each. The paper size above is width across the tape by length
        along it — the order Brother's driver uses, and inches because the Windows driver is labelled
        that way ({spec.tape} mm tape is the <strong>{inches(spec.tape)}"</strong> paper). Set scaling
        to <strong>100 %</strong>, not "fit to page": a mismatched paper size makes the driver shrink
        the strip, which is how a QR stops scanning.
      </p>

      <div className="ai-labels-picker">
        {assets.map((a) => (
          <label key={a.tag} className={"ai-pick" + (selected.has(a.tag) ? " is-on" : "")}>
            <input type="checkbox" checked={selected.has(a.tag)} onChange={() => onToggle(a.tag)} />
            <span className="ai-mono-tag">{a.tag}</span>
            <span className="ai-pick-name">{a.name}</span>
          </label>
        ))}
      </div>

      {chosen.length > 0 && (
        <div className="ai-tape-preview">
          <div className="ai-eyebrow">Preview · actual size</div>
          {chosen.map((a) => (
            <div className="ai-tape-strip" key={a.tag}>
              <TapeStrip tag={a.tag} spec={spec} payload={payload} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   14. ROOT
   ========================================================================== */

export default function AssetInventory({ user, onSignOut }) {
  const [reference, setReference] = useState(null);
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [labelSel, setLabelSel] = useState(new Set());
  const [flash, setFlash] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  };

  /* ── Tape printing ────────────────────────────────────────────────────────
     The tape loaded in the printer is a property of the desk, not of a
     session, so the width survives a reload. */

  const [tapeId, setTapeId] = useState(() => {
    const saved = typeof localStorage !== "undefined" && localStorage.getItem(TAPE_STORE_KEY);
    return TAPE_SPECS.some((s) => s.id === saved) ? saved : DEFAULT_TAPE;
  });
  const [tapeLength, setTapeLength] = useState(null); // null = the width's default
  const [payload, setPayload] = useState("auto");
  const [printJob, setPrintJob] = useState(null);

  const spec = useMemo(() => {
    const base = tapeSpec(tapeId);
    return tapeLength == null ? base : { ...base, length: tapeLength };
  }, [tapeId, tapeLength]);

  const setTape = (id) => {
    setTapeId(id);
    setTapeLength(null); // each width has its own sensible default length
    try {
      localStorage.setItem(TAPE_STORE_KEY, id);
    } catch {
      /* private mode — the width just will not persist */
    }
  };

  // An emptied field means "whatever this width normally uses", not zero.
  const setLength = (raw) => {
    if (String(raw).trim() === "") return setTapeLength(null);
    const mm = Math.round(Number(raw));
    if (!Number.isFinite(mm)) return;
    setTapeLength(Math.min(TAPE_LENGTH_MAX, Math.max(TAPE_LENGTH_MIN, mm)));
  };

  // `id` makes each request distinct, so printing the same tags twice in a row
  // still fires twice.
  const printLabels = (tags) => {
    if (!tags.length) return;
    setPrintJob((prev) => ({ id: (prev?.id ?? 0) + 1, tags, spec, payload }));
  };

  /* The dialog freezes the document the instant it opens, so the tape pages
     have to be in the DOM *and* laid out at the @page size first — one frame
     to commit them, a second to let layout settle. */
  useEffect(() => {
    if (!printJob) return;
    const clear = () => setPrintJob(null);
    window.addEventListener("afterprint", clear);

    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => window.print());
    });

    return () => {
      window.removeEventListener("afterprint", clear);
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [printJob]);

  const printerValue = useMemo(
    () => ({ spec, payload, setTape, setLength, setPayload, print: printLabels }),
    [spec, payload],
  );

  /* ── Loading ──────────────────────────────────────────────────────────────
     Every mutation re-reads the register rather than patching state from the
     response. The server owns the invariants — assigning an asset also moves
     its department, a status change releases the holder, offboarding flags
     everything a person holds — so guessing the resulting state in the browser
     is how a UI ends up disagreeing with the database. One extra round trip is
     the cheaper mistake. */

  const reload = async () => {
    const [assetRows, peopleRows, overview] = await Promise.all([
      api.assets(),
      api.people(),
      api.overview(),
    ]);
    setAssets(assetRows.map(toAsset));
    setEmployees(peopleRows.map(toPerson));
    // The feed only needs what is recent; a single asset's full history is
    // fetched when it is opened, and merged in by mergeEvents.
    mergeEvents(overview.recent_events);
  };

  // Events arrive from two places and overlap. Keyed by event_id so the same
  // row from /api/overview and from /api/assets/:tag cannot appear twice.
  const mergeEvents = (rows) =>
    setEvents((prev) => {
      const byId = new Map(prev.map((e) => [e.id, e]));
      for (const row of rows) {
        const e = toEvent(row);
        byId.set(e.id, e);
      }
      return [...byId.values()];
    });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ref = await api.reference();
        if (!alive) return;
        setReference(ref);
        await reload();
      } catch (err) {
        if (alive) setLoadError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // The detail panel shows an asset's whole timeline, which the list endpoint
  // does not carry. Fetch it when a tag is selected.
  useEffect(() => {
    if (!selectedTag) return;
    let alive = true;
    api
      .asset(selectedTag)
      .then((full) => {
        if (alive) mergeEvents(full.events);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [selectedTag]);

  /* ── Mutations ────────────────────────────────────────────────────────────
     The server's refusals are the useful ones — "Tomas Ferreira has been
     offboarded", "AST-0101 is already charged to ITSC" — so they go straight to
     the toast rather than being replaced with something generic. */

  const run = async (fn, successToast) => {
    try {
      const result = await fn();
      await reload();
      if (selectedTag) {
        await api.asset(selectedTag).then((full) => mergeEvents(full.events)).catch(() => {});
      }
      if (successToast) showToast(successToast);
      return result;
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Something went wrong.");
      return null;
    }
  };

  /* POST /api/assets/:tag/events — the server writes the event row, applies the
     change in the same transaction, and returns the updated asset. Status and
     holder are never changed without a corresponding event; the log is what
     makes this defensible in an audit. */
  const handleAction = (tag, kind, payload) => {
    const body = { kind, note: payload.note?.trim() || undefined };
    let done = "Recorded";

    if (kind === "assign") {
      body.holderId = payload.holder;
      done = `Assigned to ${employees.find((e) => e.id === payload.holder)?.name ?? "holder"}`;
    } else if (kind === "status") {
      body.status = STATUS_CODES[payload.status];
      done = "Status updated";
    } else if (kind === "transfer") {
      body.departmentCode = payload.department;
      done = "Department updated";
    } else if (kind === "return") {
      done = "Returned to stock";
    } else if (kind === "audit") {
      done = "Audit recorded";
    }

    return run(() => api.recordEvent(tag, body), done);
  };

  const handleStaffStatus = (id, statusLabelValue) => {
    const person = employees.find((e) => e.id === id);
    const status = STAFF_CODES[statusLabelValue];
    const atRisk = AT_RISK.includes(statusLabelValue);
    // employees_exit_date_required: notice and offboarded need a date. Two weeks
    // out is a placeholder the admin can correct, not a guess about the person.
    const exitDate = atRisk
      ? person?.exitDate || new Date(Date.now() + 12096e5).toISOString().slice(0, 10)
      : null;

    return run(async () => {
      const updated = await api.updatePerson(id, { status, exitDate });
      const flagged = updated.flagged_for_recovery ?? [];
      showToast(
        flagged.length
          ? `${flagged.length} ${flagged.length === 1 ? "item" : "items"} flagged for recovery`
          : `${updated.name} set to ${statusLabelValue}`,
      );
      return updated;
    });
  };

  const openAsset = (tag) => {
    setTab("assets");
    setSelectedTag(tag);
    setFlash(tag);
    setTimeout(() => setFlash(null), 1100);
  };

  const openPerson = (id) => {
    setTab("people");
    setSelectedPerson(id);
  };

  // Returns the created asset so RegisterForm can print the tag the database
  // actually allocated, rather than one guessed before the round trip.
  const handleCreate = (form) =>
    run(async () => {
      const created = await api.createAsset({
        description: form.name.trim(),
        categoryCode: categoryCode(form.category),
        serialNo: form.serial.trim().toUpperCase(),
        departmentCode: form.department,
        acquiredDate: form.acquired || undefined,
      });
      setLabelSel((prev) => new Set(prev).add(created.tag));
      showToast(`Registered ${created.tag}`);
      return toAsset(created);
    });

  const categoryCode = (name) =>
    reference?.categories.find((c) => c.name === name)?.code ?? null;

  if (loading) {
    return (
      <Shell>
        <div className="ai-boot">Loading the register…</div>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <div className="ai-boot ai-boot-error">
          <p className="ai-empty-title">Could not load the register.</p>
          <p className="ai-panel-empty">{loadError}</p>
          <button className="ai-btn" onClick={() => window.location.reload()}>Try again</button>
        </div>
      </Shell>
    );
  }

  const can = (p) => hasPermission(user, p);
  const recoveryCount = assets.filter((a) => {
    const h = a.holder && employees.find((e) => e.id === a.holder);
    return h && AT_RISK.includes(h.status);
  }).length;

  const refValue = {
    departments: reference?.departments ?? [],
    categories: reference?.categories ?? [],
    categoryCode,
    // Where returned and newly registered equipment lives. Server-side org
    // policy (STOCK_DEPT), so the form must not hardcode its own guess.
    stockDept: reference?.stockDept ?? null,
  };

  return (
    <Shell printJob={printJob}>
      <LabelPrinter.Provider value={printerValue}>
      <Reference.Provider value={refValue}>
      <div className="ai-app">
        <header className="ai-top">
          <div className="ai-brand">
            <span className="ai-brand-mark">▣</span>
            <span className="ai-brand-name">Asset Inventory</span>
            <span className="ai-brand-org">{ORG_NAME}</span>
          </div>
          <div className="ai-user">
            <div className="ai-user-meta">
              <span className="ai-user-name">{user.name}</span>
              <span className="ai-user-role">{roleLabel(user.role)}</span>
            </div>
            <ThemeToggle />
            <button className="ai-btn ai-btn-quiet ai-btn-sm" onClick={onSignOut}>Sign out</button>
          </div>
        </header>

        <nav className="ai-tabs">
          {[
            ["overview", "Overview", recoveryCount],
            ["assets", "Assets", 0],
            ["people", "People", 0],
            ["scan", "Scan", 0],
            ["labels", "Labels", 0],
          ].map(([key, label, badge]) => (
            <button key={key} className={"ai-tab" + (tab === key ? " is-on" : "")} onClick={() => setTab(key)}>
              {label}
              {badge > 0 && <span className="ai-badge">{badge}</span>}
            </button>
          ))}
        </nav>

        {tab === "overview" && (
          <Overview
            assets={assets}
            employees={employees}
            events={events}
            onOpenAsset={openAsset}
            onOpenPerson={openPerson}
          />
        )}

        {tab === "assets" && (
          <AssetsView
            assets={assets}
            employees={employees}
            events={events}
            user={user}
            selectedTag={selectedTag}
            onSelect={setSelectedTag}
            onAction={handleAction}
            onCreate={handleCreate}
            onOpenPerson={openPerson}
            onReload={reload}
            flash={flash}
          />
        )}

        {tab === "people" && (
          <PeopleView
            employees={employees}
            assets={assets}
            user={user}
            selectedId={selectedPerson}
            onSelect={setSelectedPerson}
            onStaffStatus={handleStaffStatus}
            onOpenAsset={openAsset}
            onSynced={reload}
          />
        )}

        {tab === "scan" && (
          <ScanView
            assets={assets}
            employees={employees}
            user={user}
            onOpenAsset={openAsset}
            onOpenPerson={openPerson}
            onAction={handleAction}
          />
        )}

        {tab === "labels" && (
          <LabelSheet
            assets={assets}
            selected={labelSel}
            canPrint={can("assign")}
            onToggle={(tag) =>
              setLabelSel((prev) => {
                const next = new Set(prev);
                next.has(tag) ? next.delete(tag) : next.add(tag);
                return next;
              })
            }
            onSelectAll={(all) => setLabelSel(all ? new Set(assets.map((a) => a.tag)) : new Set())}
          />
        )}

        {toast && <div className="ai-toast">{toast}</div>}
      </div>
      </Reference.Provider>
      </LabelPrinter.Provider>
    </Shell>
  );
}

/* ==========================================================================
   15. STYLES
   ========================================================================== */

function Shell({ children, printJob }) {
  return (
    <div className={"ai-root" + (printJob ? " is-printing-labels" : "")}>
      <style>{CSS}</style>
      {children}
      <TapePrintLayer job={printJob} />
    </div>
  );
}

// Exported so Login.jsx renders in the same design language without duplicating
// the tokens. Costs nothing extra: index.js loads both modules anyway.
export const CSS = `
body { margin: 0; }

/* ── Palette ────────────────────────────────────────────────────────────────
   Everything visual is a token, so night mode is a second palette rather than a
   second stylesheet. Two rules make that hold:

   1. Tokens are the only place a colour is named. A literal hex anywhere else is
      a bug — it will be wrong in one of the two themes. The one deliberate
      exception is the property tag: --plate-* below is fixed light in both
      themes, because it is a printable label and the QR code carries a baked-in
      white quiet zone. Inverted QR codes fail on a lot of scanners, and a label
      that renders dark on screen and light on paper is worse than either.
   2. Anything sitting ON a coloured surface needs its own paired token
      (--on-signal, --on-red, --on-solid). White text is only legible while the
      accent behind it is dark, and in night mode the accents are the light part.
   ────────────────────────────────────────────────────────────────────────── */

.ai-root {
  --ink: #16202B;
  --ink-2: #3C4C5C;
  --zinc: #6D7C8A;
  --paper: #EFF2F4;
  --card: #FFFFFF;
  --rule: #D5DBE1;
  --hair: #EAEEF1;
  --hover: #F7F9FA;
  --signal: #0E7C86;
  --signal-dark: #0A5F67;
  --on-signal: #FFFFFF;
  --amber: #A35A08;
  --amber-bg: #FBF0DF;
  --red: #9C2A1E;
  --red-bg: #FAE7E4;
  --on-red: #FFFFFF;
  --ok-bg: #E3EFE9; --ok-fg: #1F6248;
  --out-bg: #E1EEF0; --out-fg: #0A5F67;
  --dead-bg: #E7E9EB; --dead-fg: #5C6873;

  /* inverted surfaces — avatar, selected segment, toast */
  --solid: #16202B;
  --on-solid: #FFFFFF;

  --placeholder: #9AA6B1;
  --flash: #FCEFD6;
  --risk-bg: #FEFAF9;
  --shadow: rgba(22,32,43,0.14);

  /* the printed label: fixed in both themes, see note above */
  --plate-bg: #FFFFFF;
  --plate-ink: #16202B;
  --plate-ink-2: #3C4C5C;
  --plate-zinc: #6D7C8A;
  --sans: 'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  --mono: 'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace;

  font-family: var(--sans);
  color: var(--ink);
  background: var(--paper);
  min-height: 100vh;
  font-size: 14px; line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

/* ── Night mode ─────────────────────────────────────────────────────────────
   Three states. An explicit choice wins in both directions; with no choice
   stored, the OS decides. src/theme.js sets data-theme on <html>, and
   color-scheme goes with it so native scrollbars and <select> popups follow
   instead of staying stubbornly white.

   --signal-dark is the hover shade, and in night mode "darker" is the wrong
   direction — a hover has to move toward the light. Hence #4CC4CF, brighter
   than --signal rather than dimmer. Same token, opposite intent. */

html[data-theme="dark"] { color-scheme: dark; }
html[data-theme="dark"] body { background: #10171E; }
html[data-theme="dark"] .ai-root {
  --ink: #E7EDF3;
  --ink-2: #B6C2CE;
  --zinc: #8A98A6;
  --paper: #10171E;
  --card: #18212B;
  --rule: #2F3B47;
  --hair: #232E39;
  --hover: #1F2A35;
  --signal: #35A9B4;
  --signal-dark: #4CC4CF;
  --on-signal: #06191C;
  --amber: #E3A961;
  --amber-bg: #33260F;
  --red: #EC8B7E;
  --red-bg: #3A1D18;
  --on-red: #2A100C;
  --ok-bg: #172E25; --ok-fg: #74D2A8;
  --out-bg: #122C30; --out-fg: #67CCD6;
  --dead-bg: #232B32; --dead-fg: #9AA6B1;

  --solid: #E7EDF3;
  --on-solid: #10171E;

  --placeholder: #6C7A85;
  --flash: #3B2F12;
  --risk-bg: #241A18;
  --shadow: rgba(0,0,0,0.5);
}

@media (prefers-color-scheme: dark) {
  html:not([data-theme="light"]) { color-scheme: dark; }
  html:not([data-theme="light"]) body { background: #10171E; }
  html:not([data-theme="light"]) .ai-root {
  --ink: #E7EDF3;
  --ink-2: #B6C2CE;
  --zinc: #8A98A6;
  --paper: #10171E;
  --card: #18212B;
  --rule: #2F3B47;
  --hair: #232E39;
  --hover: #1F2A35;
  --signal: #35A9B4;
  --signal-dark: #4CC4CF;
  --on-signal: #06191C;
  --amber: #E3A961;
  --amber-bg: #33260F;
  --red: #EC8B7E;
  --red-bg: #3A1D18;
  --on-red: #2A100C;
  --ok-bg: #172E25; --ok-fg: #74D2A8;
  --out-bg: #122C30; --out-fg: #67CCD6;
  --dead-bg: #232B32; --dead-fg: #9AA6B1;

  --solid: #E7EDF3;
  --on-solid: #10171E;

  --placeholder: #6C7A85;
  --flash: #3B2F12;
  --risk-bg: #241A18;
  --shadow: rgba(0,0,0,0.5);
  }
}

.ai-root *, .ai-root *::before, .ai-root *::after { box-sizing: border-box; }

.ai-eyebrow {
  font-family: var(--mono); font-size: 10px; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--zinc);
}
.ai-mono-tag { font: 600 11px var(--mono); letter-spacing: 0.06em; white-space: nowrap; }
.ai-note { font-size: 12px; color: var(--zinc); margin: 10px 0 0; }
.ai-note code { font-family: var(--mono); font-size: 11px; background: var(--paper); padding: 1px 4px; border-radius: 2px; }
.ai-note-inline { margin: 0; align-self: center; }
.ai-panel-empty { font-size: 13px; color: var(--zinc); margin: 8px 0 0; }
.ai-theme-btn { padding: 5px 9px; line-height: 1; gap: 5px; }
.ai-theme-btn span[aria-hidden] { font-size: 14px; }
.ai-theme-auto {
  font: 600 8px var(--mono); letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--zinc);
}

.ai-boot {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; min-height: 60vh; color: var(--zinc); font-size: 14px;
}
.ai-boot-error { color: var(--ink); text-align: center; }

/* buttons & inputs */
.ai-btn {
  font: 500 13px var(--sans); padding: 8px 14px;
  border: 1px solid var(--rule); background: var(--card); color: var(--ink);
  border-radius: 3px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  transition: background 120ms, border-color 120ms;
}
.ai-btn:hover:not(:disabled) { border-color: var(--zinc); background: var(--hover); }
.ai-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.ai-btn-primary { background: var(--signal); border-color: var(--signal); color: var(--on-signal); }
.ai-btn-primary:hover:not(:disabled) { background: var(--signal-dark); border-color: var(--signal-dark); }
.ai-btn-quiet { background: transparent; }
.ai-btn-sm { padding: 5px 10px; font-size: 12px; }
.ai-btn-ms { width: 100%; padding: 11px; }
.ai-grow { flex: 1; }
.ai-full { width: 100%; margin-top: 10px; }

.ai-input, .ai-select {
  font: 400 13px var(--sans); padding: 8px 10px;
  border: 1px solid var(--rule); background: var(--card); color: var(--ink);
  border-radius: 3px; width: 100%;
}
.ai-input::placeholder { color: var(--placeholder); }
.ai-input-lg { font: 600 20px var(--mono); padding: 14px; letter-spacing: 0.06em; }
.ai-select { cursor: pointer; }
.ai-field-label { display: block; font-size: 12px; color: var(--zinc); margin: 10px 0 4px; }
.ai-icon-btn { border: 0; background: none; color: var(--zinc); font-size: 15px; cursor: pointer; padding: 4px 6px; border-radius: 3px; }
.ai-icon-btn:hover { background: var(--paper); color: var(--ink); }
.ai-error { font-size: 13px; color: var(--red); margin: 12px 0 0; }

.ai-btn:focus-visible, .ai-input:focus-visible, .ai-select:focus-visible, .ai-tab:focus-visible,
.ai-seg-btn:focus-visible, .ai-table tr:focus-visible, .ai-person:focus-visible,
.ai-icon-btn:focus-visible, .ai-feed-row:focus-visible, .ai-recovery-item:focus-visible,
.ai-recovery-person:focus-visible, .ai-own-person:focus-visible {
  outline: 2px solid var(--signal); outline-offset: 1px;
}

/* the property tag: signature element */
.ai-plate {
  background: var(--plate-bg); color: var(--plate-ink);
  border: 1px solid var(--plate-ink); border-radius: 2px;
  padding: 10px 12px 8px; text-align: center;
  display: inline-flex; flex-direction: column; align-items: center; gap: 6px;
  box-shadow: 2px 2px 0 var(--shadow);
}
.ai-plate-eyebrow {
  font-family: var(--mono); font-size: 7px; font-weight: 600;
  letter-spacing: 0.13em; text-transform: uppercase; color: var(--plate-ink-2);
  max-width: 150px; line-height: 1.35;
}
.ai-plate-tag {
  font-family: var(--mono); font-size: 15px; font-weight: 600; letter-spacing: 0.18em;
  padding-top: 6px; border-top: 1px solid var(--plate-ink); width: 100%;
}
.ai-plate-foot {
  font-family: var(--mono); font-size: 7px; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--plate-zinc);
}
.ai-qr-fail { font-size: 12px; color: var(--amber); }

/* avatar */
.ai-avatar {
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--solid); color: var(--on-solid); border-radius: 50%;
  font-family: var(--mono); font-weight: 600; letter-spacing: 0.04em;
  flex: 0 0 auto;
}

/* chips */
.ai-chip {
  display: inline-block; padding: 3px 8px; border-radius: 2px;
  font-family: var(--mono); font-size: 9px; font-weight: 600;
  letter-spacing: 0.1em; text-transform: uppercase; white-space: nowrap;
}
.ai-chip-ok { background: var(--ok-bg); color: var(--ok-fg); }
.ai-chip-out { background: var(--out-bg); color: var(--out-fg); }
.ai-chip-warn { background: var(--amber-bg); color: var(--amber); }
.ai-chip-bad { background: var(--red-bg); color: var(--red); }
.ai-chip-dead { background: var(--dead-bg); color: var(--dead-fg); }

/* sign in */
.ai-gate { min-height: 100vh; display: grid; place-items: center; padding: 24px; }
.ai-gate-card {
  background: var(--card); border: 1px solid var(--rule); border-radius: 4px;
  max-width: 720px; width: 100%; display: grid; grid-template-columns: 260px 1fr;
}
.ai-gate-card > .ai-plate { margin: 32px; align-self: center; justify-self: center; }
.ai-gate-body { padding: 40px 40px 40px 8px; border-left: 1px solid var(--rule); }
.ai-gate-title { font-size: 26px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 8px; }
.ai-gate-sub { color: var(--ink-2); margin: 0 0 24px; max-width: 36ch; }
.ai-gate-dev { margin-top: 24px; padding-top: 18px; border-top: 1px dashed var(--rule); }
.ai-seg { display: flex; margin-top: 8px; border: 1px solid var(--rule); border-radius: 3px; overflow: hidden; }
.ai-seg-wrap { flex-wrap: wrap; }
.ai-seg-btn {
  flex: 1; font: 500 12px var(--sans); padding: 7px 6px; white-space: nowrap;
  background: var(--card); border: 0; border-right: 1px solid var(--rule);
  color: var(--zinc); cursor: pointer;
}
.ai-seg-btn:last-child { border-right: 0; }
.ai-seg-btn.is-on { background: var(--solid); color: var(--on-solid); }

/* frame */
.ai-app { max-width: 1340px; margin: 0 auto; padding: 0 20px 60px; }
.ai-top { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 0 14px; }
.ai-brand { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.ai-brand-mark { color: var(--signal); font-size: 16px; }
.ai-brand-name { font-size: 16px; font-weight: 600; letter-spacing: -0.01em; }
.ai-brand-org {
  font-family: var(--mono); font-size: 10px; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--zinc);
}
.ai-user { display: flex; align-items: center; gap: 14px; }
.ai-user-meta { display: flex; flex-direction: column; align-items: flex-end; line-height: 1.3; }
.ai-user-name { font-size: 13px; font-weight: 500; }
.ai-user-role {
  font-family: var(--mono); font-size: 9px; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--signal);
}

.ai-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--rule); overflow-x: auto; }
.ai-tab {
  font: 500 13px var(--sans); padding: 9px 16px; white-space: nowrap;
  background: none; border: 0; border-bottom: 2px solid transparent;
  color: var(--zinc); cursor: pointer; margin-bottom: -1px;
  display: inline-flex; align-items: center; gap: 7px;
}
.ai-tab:hover { color: var(--ink); }
.ai-tab.is-on { color: var(--ink); border-bottom-color: var(--signal); }
.ai-badge {
  background: var(--red); color: var(--on-red); border-radius: 9px; padding: 1px 6px;
  font: 600 10px var(--mono);
}

/* panels */
.ai-panel { background: var(--card); border: 1px solid var(--rule); border-radius: 3px; padding: 18px; }
.ai-panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
.ai-panel-title { font-size: 17px; font-weight: 600; margin: 3px 0 0; letter-spacing: -0.01em; }
.ai-panel-alert { border-left: 3px solid var(--amber); }

/* overview */
.ai-overview { padding-top: 18px; display: flex; flex-direction: column; gap: 16px; }
.ai-stats {
  display: grid; grid-template-columns: repeat(5, 1fr); gap: 1px;
  background: var(--rule); border: 1px solid var(--rule);
}
.ai-stat { background: var(--card); padding: 12px 14px; display: flex; flex-direction: column; gap: 2px; }
.ai-stat-label {
  font-family: var(--mono); font-size: 9px; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--zinc);
}
.ai-stat-value { font: 600 20px var(--mono); letter-spacing: -0.01em; }

.ai-exposure { text-align: right; }
.ai-exposure-value { display: block; font: 600 22px var(--mono); color: var(--amber); text-align: right; }

.ai-recovery { list-style: none; margin: 0; padding: 0; }
.ai-recovery > li { border-top: 1px solid var(--hair); padding: 12px 0; }
.ai-recovery-person {
  display: flex; align-items: center; gap: 11px; width: 100%;
  background: none; border: 0; padding: 0; cursor: pointer; text-align: left;
}
.ai-recovery-meta { flex: 1; display: flex; flex-direction: column; line-height: 1.35; min-width: 0; }
.ai-recovery-name { font-size: 14px; font-weight: 600; }
.ai-recovery-sub { font-size: 12px; color: var(--zinc); }
.ai-recovery-items { display: flex; flex-direction: column; gap: 1px; margin: 9px 0 0 45px; }
.ai-recovery-item {
  display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: baseline;
  background: var(--paper); border: 0; border-left: 2px solid var(--amber);
  padding: 7px 10px; cursor: pointer; text-align: left; font-family: var(--sans);
}
.ai-recovery-item:hover { background: var(--amber-bg); }
.ai-recovery-item-name { font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.ai-deptable { width: 100%; border-collapse: collapse; margin-top: 10px; }
.ai-of { color: var(--zinc); font-weight: 400; }
.ai-recv-tags { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 4px 0 14px; }
.ai-recv-prefix { width: 130px; font-family: var(--mono); }
.ai-recv-start { width: 100px; font-family: var(--mono); }
.ai-deptable thead th {
  text-align: left; padding: 6px 10px 6px 0;
  font-family: var(--mono); font-size: 9px; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--zinc);
  border-bottom: 1px solid var(--rule);
}
.ai-deptable tbody tr { border-bottom: 1px solid var(--hair); }
.ai-deptable tbody tr.is-empty { opacity: 0.4; }
.ai-deptable td { padding: 7px 10px 7px 0; }
.ai-deptable .ai-num { font: 500 12px var(--mono); white-space: nowrap; }
.ai-barcell { width: 34%; padding-right: 0 !important; }
.ai-bar { display: block; height: 6px; background: var(--signal); border-radius: 1px; min-width: 1px; }

.ai-feed { list-style: none; margin: 10px 0 0; padding: 0; }
.ai-feed li { border-top: 1px solid var(--hair); }
.ai-feed-row {
  display: grid; grid-template-columns: 80px 110px 1fr auto; gap: 12px; align-items: baseline;
  width: 100%; background: none; border: 0; padding: 9px 2px; cursor: pointer;
  text-align: left; font-family: var(--sans);
}
.ai-feed-row:hover { background: var(--hover); }
.ai-feed-type { font-size: 12px; font-weight: 600; }
.ai-feed-note { font-size: 12px; color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ai-feed-time { font: 400 10px var(--mono); color: var(--zinc); white-space: nowrap; }

/* filters */
.ai-filters { display: grid; grid-template-columns: 1fr 130px 130px 110px auto; gap: 8px; margin: 18px 0 14px; }
.ai-filters-people { grid-template-columns: 320px 1fr; }

/* split */
.ai-split { display: block; }
.ai-split.has-detail { display: grid; grid-template-columns: minmax(0,1fr) 372px; gap: 16px; align-items: start; }

/* table */
.ai-table-wrap { background: var(--card); border: 1px solid var(--rule); border-radius: 3px; overflow-x: auto; }
.ai-table { width: 100%; border-collapse: collapse; }
.ai-table thead th {
  text-align: left; padding: 9px 12px;
  font-family: var(--mono); font-size: 9px; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--zinc);
  border-bottom: 1px solid var(--rule); white-space: nowrap;
}
.ai-table tbody tr { border-bottom: 1px solid var(--hair); cursor: pointer; }
.ai-table tbody tr:last-child { border-bottom: 0; }
.ai-table tbody tr:hover { background: var(--hover); }
.ai-table tbody tr.is-selected { background: var(--out-bg); }
.ai-table tbody tr.is-flash { animation: ai-flash 1.1s ease-out; }
@keyframes ai-flash { 0%, 40% { background: var(--flash); } 100% { background: transparent; } }
.ai-table td { padding: 9px 12px; vertical-align: middle; }
.ai-td-tag { font: 600 12px var(--mono); letter-spacing: 0.06em; white-space: nowrap; }
.ai-td-name { display: block; font-weight: 500; }
.ai-td-serial { display: block; font: 400 11px var(--mono); color: var(--zinc); }
.ai-td-dim { color: var(--ink-2); white-space: nowrap; }
.ai-dept {
  display: inline-block; padding: 2px 7px; border: 1px solid var(--rule); border-radius: 2px;
  font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: 0.08em;
  color: var(--ink-2); background: var(--paper); white-space: nowrap;
}
.ai-td-holder { display: inline-flex; align-items: center; gap: 7px; white-space: nowrap; }
.ai-td-holder.is-risky { color: var(--red); font-weight: 500; }
.ai-flag {
  display: inline-flex; align-items: center; justify-content: center;
  width: 15px; height: 15px; border-radius: 50%;
  background: var(--red); color: var(--on-red); font: 600 10px var(--mono);
}
.ai-empty { text-align: center; padding: 40px; }
.ai-empty-title { font-weight: 500; margin: 0; }

/* ownership card */
.ai-own { border: 1px solid var(--rule); border-radius: 3px; padding: 13px; margin: 16px 0; }
.ai-own.is-risky { border-color: var(--red); background: var(--risk-bg); }
.ai-own-none { background: var(--paper); border-style: dashed; }
.ai-own-empty { font-size: 13px; color: var(--ink-2); margin: 6px 0 0; }
.ai-own-person {
  display: flex; align-items: center; gap: 12px; width: 100%; margin-top: 9px;
  background: none; border: 0; padding: 0; cursor: pointer; text-align: left;
}
.ai-own-meta { display: flex; flex-direction: column; line-height: 1.35; min-width: 0; }
.ai-own-name { font-size: 16px; font-weight: 600; letter-spacing: -0.01em; }
.ai-own-sub { font-size: 12px; color: var(--ink-2); }
.ai-own-mail { font: 400 11px var(--mono); color: var(--zinc); overflow: hidden; text-overflow: ellipsis; }
.ai-own-facts { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 11px; }
.ai-own-fact { font-size: 12px; color: var(--ink-2); }
.ai-own-warn {
  font-size: 12px; color: var(--red); margin: 11px 0 0;
  padding-top: 10px; border-top: 1px solid var(--red-bg); font-weight: 500;
}

/* scan */
.ai-scan { display: grid; grid-template-columns: minmax(0,1fr) 300px; gap: 16px; padding-top: 18px; align-items: start; }
.ai-scan-main { display: flex; flex-direction: column; gap: 16px; }
.ai-scan-help { font-size: 13px; color: var(--ink-2); margin: 8px 0 14px; max-width: 54ch; }
.ai-camera { margin: 0 0 16px; }
.ai-result-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
.ai-result-tag { color: var(--zinc); }
.ai-result-name { font-size: 20px; font-weight: 600; margin: 3px 0 3px; letter-spacing: -0.015em; }
.ai-result-sub { font: 400 11px var(--mono); color: var(--zinc); }
.ai-result-actions { border-top: 1px solid var(--hair); padding-top: 14px; }
.ai-log { list-style: none; margin: 10px 0 0; padding: 0; }
.ai-log li { border-bottom: 1px solid var(--hair); }
.ai-log li button, .ai-held li button {
  display: grid; grid-template-columns: auto 1fr auto; gap: 9px; align-items: baseline;
  width: 100%; background: none; border: 0; padding: 8px 2px; cursor: pointer;
  text-align: left; font-family: var(--sans);
}
.ai-log li button:hover, .ai-held li button:hover { background: var(--hover); }
.ai-log-name { font-size: 12px; color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ai-log-time { font: 400 10px var(--mono); color: var(--zinc); }

/* register */
.ai-register { margin-top: 18px; max-width: 620px; }
.ai-form { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 14px; margin-bottom: 16px; }
.ai-form-row { display: block; }
.ai-register-done { font-size: 13px; color: var(--ink-2); margin: 8px 0 18px; max-width: 52ch; }
.ai-register-tag {
  margin-bottom: 18px; display: flex; flex-wrap: wrap; gap: 22px; align-items: flex-start;
}

/* detail */
.ai-detail {
  background: var(--card); border: 1px solid var(--rule); border-radius: 3px;
  position: sticky; top: 12px; max-height: calc(100vh - 40px);
  display: flex; flex-direction: column;
  animation: ai-slide 180ms ease-out;
}
@keyframes ai-slide { from { opacity: 0; transform: translateX(8px); } to { opacity: 1; transform: none; } }
.ai-detail-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 12px 14px; border-bottom: 1px solid var(--rule); }
.ai-detail-tag { font: 600 16px var(--mono); letter-spacing: 0.1em; }
.ai-detail-person { font-size: 16px; font-weight: 600; }
.ai-detail-scroll { padding: 16px 14px 24px; overflow-y: auto; }
.ai-detail-name { font-size: 17px; font-weight: 600; margin: 0 0 8px; letter-spacing: -0.01em; }
.ai-detail-tagwrap { display: flex; justify-content: center; margin: 4px 0 18px; }

.ai-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; margin: 0 0 18px; }
.ai-field dt {
  font-family: var(--mono); font-size: 9px; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--zinc); margin-bottom: 3px;
}
.ai-field dd { margin: 0; font-size: 13px; }
.ai-field dd.is-mono { font-family: var(--mono); font-size: 12px; }

.ai-actions { border-top: 1px solid var(--rule); padding-top: 14px; }
.ai-action-row { display: flex; gap: 8px; margin-top: 10px; align-items: stretch; }
.ai-action-row .ai-btn { white-space: nowrap; }
.ai-readonly { border-top: 1px solid var(--rule); padding-top: 14px; margin-bottom: 0; font-size: 13px; color: var(--ink-2); }

.ai-history { border-top: 1px solid var(--rule); margin-top: 20px; padding-top: 14px; }
.ai-timeline { list-style: none; margin: 10px 0 0; padding: 0; }
.ai-timeline li { padding: 10px 0 10px 12px; border-left: 2px solid var(--rule); position: relative; }
.ai-timeline li::before {
  content: ''; position: absolute; left: -4px; top: 15px;
  width: 6px; height: 6px; background: var(--signal); border-radius: 50%;
}
.ai-tl-head { display: flex; justify-content: space-between; gap: 8px; align-items: baseline; }
.ai-tl-type { font-size: 12px; font-weight: 600; }
.ai-tl-time { font: 400 10px var(--mono); color: var(--zinc); white-space: nowrap; }
.ai-tl-note { margin: 3px 0; font-size: 12px; color: var(--ink-2); }
.ai-tl-by { font: 400 10px var(--mono); letter-spacing: 0.06em; color: var(--zinc); }

.ai-held { list-style: none; margin: 10px 0 0; padding: 0; }
.ai-held-name { font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ai-held-days { font: 500 11px var(--mono); color: var(--zinc); }

/* people */
.ai-people {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1px;
  background: var(--rule); border: 1px solid var(--rule); border-radius: 3px;
}
.ai-person {
  display: flex; align-items: center; gap: 11px; padding: 12px 14px;
  background: var(--card); border: 0; cursor: pointer; text-align: left;
  font-family: var(--sans);
}
.ai-import-text {
  width: 100%; min-height: 190px; margin: 4px 0 14px; padding: 10px 12px;
  font: 400 11px/1.5 var(--mono); color: var(--ink); background: var(--paper);
  border: 1px solid var(--rule); border-radius: 3px; resize: vertical;
  white-space: pre; overflow-wrap: normal; overflow-x: auto;
}
.ai-import-problems {
  display: flex; flex-direction: column; gap: 1px; margin-top: 12px;
  background: var(--rule); border: 1px solid var(--rule); border-radius: 3px;
  max-height: 340px; overflow-y: auto;
}
.ai-import-row { display: flex; gap: 12px; padding: 8px 12px; background: var(--card); font-size: 12px; }
.ai-import-row.is-bad { box-shadow: inset 3px 0 0 var(--red); }
.ai-import-msgs { display: flex; flex-direction: column; gap: 3px; color: var(--ink-2); }
.ai-import-msgs .is-warn { color: var(--amber); }

.ai-org-sync { display: flex; align-items: center; gap: 12px; justify-content: flex-end; }
.ai-org-count { font: 600 12px var(--mono); color: var(--zinc); letter-spacing: 0.04em; }
.ai-note-inline.is-warn { color: var(--amber); }

.ai-person:hover { background: var(--hover); }
.ai-person.is-selected { background: var(--out-bg); }
.ai-person.is-risky { box-shadow: inset 3px 0 0 var(--red); }
.ai-person-meta { flex: 1; display: flex; flex-direction: column; line-height: 1.35; min-width: 0; }
.ai-person-name { font-size: 14px; font-weight: 600; }
.ai-person-sub { font-size: 12px; color: var(--zinc); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ai-person-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.ai-person-count { font: 500 10px var(--mono); color: var(--zinc); }
.ai-person-hero { display: flex; align-items: center; gap: 13px; margin-bottom: 18px; }

/* labels */
.ai-labels { padding-top: 18px; }
.ai-labels-bar { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 14px; }
.ai-labels-count { font: 600 17px var(--mono); margin: 2px 0 0; }
.ai-labels-picker {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 1px;
  background: var(--rule); border: 1px solid var(--rule); border-radius: 3px; margin-bottom: 22px;
}
.ai-pick { background: var(--card); padding: 9px 12px; display: flex; align-items: center; gap: 9px; cursor: pointer; font-size: 13px; }
.ai-pick.is-on { background: var(--out-bg); }
.ai-pick input { accent-color: var(--signal); }
.ai-pick-name { color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* tape setup */
.ai-tape-setup {
  display: flex; flex-wrap: wrap; gap: 18px; align-items: flex-end;
  padding: 14px 16px; margin-bottom: 10px;
  border: 1px solid var(--rule); border-radius: 3px; background: var(--card);
}
.ai-tape-ctl { display: flex; flex-direction: column; gap: 5px; }
.ai-tape-ctl select, .ai-tape-ctl input {
  font: 500 13px var(--sans); color: var(--ink); background: var(--paper);
  border: 1px solid var(--rule); border-radius: 3px; padding: 7px 9px;
}
.ai-tape-ctl input { width: 96px; font-family: var(--mono); }
.ai-tape-page-size { font: 600 13px var(--mono); color: var(--ink); padding: 7px 0; }
.ai-tape-inch { font-weight: 500; color: var(--zinc); }
.ai-tape-note { font-size: 12px; color: var(--zinc); margin: 0 0 8px; line-height: 1.5; }
.ai-tape-note.is-warn { color: var(--amber); }
.ai-tape-note:last-of-type { margin-bottom: 22px; }

/* the tape label — every dimension physical, because the tape is */
.ai-tape {
  box-sizing: border-box; overflow: hidden;
  display: flex; align-items: center;
  background: #FFFFFF; color: #000000;
  font-family: var(--mono); line-height: 1;
}
.ai-tape-text { min-width: 0; display: flex; flex-direction: column; justify-content: center; gap: 0.4mm; }
.ai-tape-org, .ai-tape-tag, .ai-tape-foot { white-space: nowrap; overflow: hidden; text-overflow: clip; }
.ai-tape-org { font-weight: 600; letter-spacing: 0.1em; }
.ai-tape-tag { font-weight: 700; letter-spacing: 0.1em; }
.ai-tape-foot { letter-spacing: 0.08em; text-transform: uppercase; color: #333333; }

/* preview: shown at true size, so what looks unreadable here is unreadable there */
.ai-tape-preview { display: flex; flex-direction: column; gap: 10px; }
.ai-tape-strip { border: 1px dashed var(--rule); border-radius: 2px; padding: 3px; width: max-content; }
.ai-register-strip { display: flex; flex-direction: column; gap: 6px; }

/* the print layer. The page is the tape's own orientation (across × along) and
   the readable strip is rotated into it, centred on the page so the transform
   needs no per-width offset maths. */
.ai-tape-print { display: none; }
.ai-tape-page { position: relative; overflow: hidden; background: #FFFFFF; }
.ai-tape-rot { position: absolute; top: 50%; left: 50%; transform-origin: 50% 50%; }

/* toast */
.ai-toast {
  position: fixed; bottom: 22px; left: 50%; transform: translateX(-50%);
  background: var(--solid); color: var(--on-solid); padding: 9px 18px; border-radius: 3px;
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: 0.1em; text-transform: uppercase; z-index: 50;
  animation: ai-rise 180ms ease-out;
}
@keyframes ai-rise { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }

/* responsive */
@media (max-width: 1000px) {
  .ai-stats { grid-template-columns: repeat(3, 1fr); }
  .ai-split.has-detail { grid-template-columns: 1fr; }
  .ai-detail { position: static; max-height: none; }
  .ai-scan { grid-template-columns: 1fr; }
  .ai-filters { grid-template-columns: 1fr 1fr; }
  .ai-filters-people { grid-template-columns: 1fr; }
  .ai-gate-card { grid-template-columns: 1fr; }
  .ai-gate-body { padding: 8px 28px 32px; border-left: 0; }
  .ai-gate-card > .ai-plate { margin: 28px auto 8px; }
}
@media (max-width: 620px) {
  .ai-app { padding: 0 12px 48px; }
  .ai-stats { grid-template-columns: repeat(2, 1fr); }
  .ai-form { grid-template-columns: 1fr; }
  .ai-fields { grid-template-columns: 1fr; }
  .ai-user-meta { display: none; }
  .ai-feed-row { grid-template-columns: 70px 1fr; row-gap: 3px; }
  .ai-feed-note, .ai-feed-time { grid-column: 1 / -1; }
  .ai-table thead th:nth-child(4), .ai-table td:nth-child(4) { display: none; }
  .ai-recovery-items { margin-left: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .ai-root *, .ai-root *::before { animation: none !important; transition: none !important; }
}

@media print {
  /* Never print night mode. Beyond the wasted toner, a dark QR quiet zone does
     not scan. */
  .ai-root {
    background: #FFFFFF;
    --ink: #16202B; --ink-2: #3C4C5C; --zinc: #6D7C8A;
    --paper: #FFFFFF; --card: #FFFFFF; --rule: #D5DBE1; --hair: #EAEEF1;
    --solid: #16202B; --on-solid: #FFFFFF; --shadow: rgba(22,32,43,0.14);
  }
  .ai-top, .ai-tabs, .ai-labels-bar, .ai-labels-picker, .ai-tape-setup,
  .ai-tape-note, .ai-tape-preview, .ai-toast, .ai-filters, .ai-action-row,
  .ai-form { display: none !important; }
  .ai-panel { border: 0; padding: 0; }
  .ai-plate { box-shadow: none; break-inside: avoid; }

  /* A tape run replaces the document outright. Leaving the app rendered is how
     a stray A4 page ends up mid-roll, and the page box is now tape-sized, so
     nothing else on this screen would fit it anyway. */
  .ai-root.is-printing-labels > .ai-app { display: none !important; }
  .ai-root.is-printing-labels .ai-tape-print { display: block; }

  /* One label, one page. break-after on the wrapper rather than the label
     itself so the label's own box stays exactly the printable area. */
  /* Exactly one page per label. break-after on the page box itself now that it
     *is* the page — and the box matching @page exactly is what stops a stray
     sliver paginating onto a second, blank label. */
  .ai-tape-page {
    break-inside: avoid; page-break-inside: avoid;
    break-after: page; page-break-after: always;
    overflow: hidden;
  }
  .ai-tape-page:last-child { break-after: auto; page-break-after: auto; }
  .ai-tape { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
}
`;